import React, { useEffect, useState, useRef } from "react";
import { db } from "../../firebase";
import {
  collection, getDocs, doc, getDoc, addDoc, updateDoc,
  query, where, limit, serverTimestamp, onSnapshot, writeBatch, increment,
} from "firebase/firestore";
import { getAuth, updatePassword, EmailAuthProvider, reauthenticateWithCredential } from "firebase/auth";
import { GoogleMap, useJsApiLoader, Marker, InfoWindow } from "@react-google-maps/api";
import { getMapCenter } from "../../utils/timezoneCoords";
import { startOfDayInTimezone, todayStringInTimezone } from "../../utils/dateFormat";
import { getDistance } from "geolib";
import { useAuth } from "../../context/AuthContext";
import { useLanguage } from "../../context/LanguageContext";
import { useBranding } from "../../context/BrandingContext";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import {
  Truck, MapPin, BarChart2, User, Package,
  Navigation, CheckCircle,
  AlertTriangle, Loader2, LogOut, Phone, Home,
  MessageSquare, Send, ChevronDown, X,
  Map as MapIcon, Globe, Crosshair, Search,
  ShoppingCart, CheckSquare, Eye, EyeOff, Lock,
  Printer, Receipt, RefreshCw, TrendingUp, WifiOff,
  Moon,
} from "lucide-react";

import { GOOGLE_MAPS_KEY, GOOGLE_MAPS_LIBRARIES } from "../../utils/googleMapsConfig";
import {
  saveClientsOffline, getClientsOffline,
  saveSectionsOffline, getSectionsOffline,
  savePendingCheckin, savePendingNote,
  getPendingCount, getMeta, clearOfflineCache,
  saveStockOffline, getStockOffline,
  saveCatalogOffline, getCatalogOffline,
  savePendingSale, updateStockItemOffline,
  getPendingSales,
} from "../../utils/offlineDB";
import { syncPendingData } from "../../utils/offlineSync";
const DEFAULT_CENTER = { lat: 25.2048, lng: 55.2708 };

// ─────────────────────────────────────────────────────────────────────────────
// Bottom tab pages
// ─────────────────────────────────────────────────────────────────────────────
const BASE_PAGES = [
  { id: "route",      Icon: Home,      labelKey: "vendor_route"   },
  { id: "map",        Icon: MapIcon,   labelKey: "vendor_map"     },
  { id: "stats",      Icon: BarChart2, labelKey: "vendor_stats"   },
  { id: "profile",    Icon: User,      labelKey: "vendor_profile" },
];

// ─────────────────────────────────────────────────────────────────────────────
// Root component
// ─────────────────────────────────────────────────────────────────────────────
function VendorDashboard() {
  const { branding } = useBranding();
  const { currentUser, companyId, logout } = useAuth();
  const { t } = useLanguage();
  const [activePage,       setActivePage]       = useState("map");
  const [isDarkMode,       setIsDarkMode]       = useState(() => localStorage.getItem("pinpoint_dark_mode") === "true");
  const [stockEnabled,       setStockEnabled]       = useState(false);
  const [stockLoading,       setStockLoading]       = useState(true);
  const [routeOptimization,  setRouteOptimization]  = useState("disabled");
  const [vendorProfile,    setVendorProfile]     = useState(null);
  const [vendorDocId,      setVendorDocId]       = useState(null);
  const [visitResetDays,      setVisitResetDays]      = useState(1);
  const [companyTimezone,     setCompanyTimezone]     = useState("Asia/Dubai");
  const [nearbyMin,           setNearbyMin]           = useState(20);
  const [nearbyMax,           setNearbyMax]           = useState(100);
  const [allowManualCheckin,  setAllowManualCheckin]  = useState(true);
  const [requireNotesOnVisit, setRequireNotesOnVisit] = useState(false);
  // Shared note modal state — lifted so any page can open it
  const [noteModal,        setNoteModal]         = useState(false);
  const [noteClient,       setNoteClient]        = useState(null);
  const [noteSaving,       setNoteSaving]        = useState(false);
  const [noteError,        setNoteError]         = useState("");
  const [noteSuccess,      setNoteSuccess]       = useState("");
  const emptyNote = { message: "", priority: "normal" };
  const [noteForm,         setNoteForm]          = useState(emptyNote);
  // Shared sell modal state
  const [sellModal,        setSellModal]         = useState(false);
  const [sellClient,       setSellClient]        = useState(null);
  // Offline state
  const [isOnline,         setIsOnline]          = useState(navigator.onLine);
  const [pendingCount,     setPendingCount]      = useState(0);
  const [syncing,          setSyncing]           = useState(false);
  const [lastSyncTime,     setLastSyncTime]      = useState(null);
  const [syncSuccess,      setSyncSuccess]       = useState("");

  // Online/offline listeners + initial pending count
  useEffect(() => {
    getPendingCount().then(setPendingCount).catch(() => {});
    getMeta("lastSyncTime").then(setLastSyncTime).catch(() => {});
    function onOnline()  { setIsOnline(true);  handleSync(); }
    function onOffline() { setIsOnline(false); }
    window.addEventListener("online",  onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online",  onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleSync() {
    if (syncing || !navigator.onLine || !companyId) return;
    setSyncing(true);
    setSyncSuccess("");
    try {
      const { synced } = await syncPendingData(companyId);
      const count = await getPendingCount();
      setPendingCount(count);
      const ts = await getMeta("lastSyncTime");
      setLastSyncTime(ts);
      if (synced > 0) {
        setSyncSuccess(`${synced} item${synced > 1 ? "s" : ""} synced.`);
        setTimeout(() => setSyncSuccess(""), 3000);
      }
    } catch {}
    setSyncing(false);
  }

  useEffect(() => {
    if (!companyId || !currentUser?.uid) return;
    async function loadCompany() {
      try {
        const [rootSnap, configSnap] = await Promise.all([
          getDoc(doc(db, "companies", companyId)),
          getDoc(doc(db, "companies", companyId, "config", "settings")),
        ]);
        if (rootSnap.exists()) {
          const data = rootSnap.data();
          setStockEnabled(data.stock_feature_enabled === true);
          setAllowManualCheckin(data.allow_manual_checkin !== false);
          setRequireNotesOnVisit(data.require_notes_on_visit === true);
          setRouteOptimization(data.route_optimization || "disabled");
        }
        // config/settings is authoritative for nearby distances and visit_reset_days
        const cfg = configSnap?.exists() ? configSnap.data()
                  : rootSnap?.exists()   ? rootSnap.data()
                  : {};
        const nearMin   = cfg.nearby_min       ?? 20;
        const nearMax   = cfg.nearby_max       ?? 100;
        const resetDays = cfg.visit_reset_days ?? 1;
        setNearbyMin(nearMin);
        setNearbyMax(nearMax);
        setVisitResetDays(resetDays);
        setCompanyTimezone(cfg.timezone || "Asia/Dubai");
        console.log("[VendorDashboard] loadCompany — nearby_min:", nearMin, "nearby_max:", nearMax, "visit_reset_days:", resetDays);
      } catch (err) {
        console.error("loadCompany:", err.message);
      } finally {
        setStockLoading(false);
      }
    }
    async function loadProfile() {
      try {
        // Use docId claim (Firestore doc ID) — differs from Firebase Auth UID for vendors
        const tokenResult = await currentUser.getIdTokenResult();
        const docId = tokenResult.claims.docId || currentUser.uid;
        setVendorDocId(docId);
        const snap = await getDoc(doc(db, "companies", companyId, "users", docId));
        if (snap.exists()) setVendorProfile(snap.data());
      } catch (err) { console.error("loadProfile:", err.message); }
    }
    loadCompany();
    loadProfile();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, currentUser?.uid]);

  const pages = stockEnabled
    ? [...BASE_PAGES.slice(0, 3), { id: "stock", Icon: Package, labelKey: "vendor_stock" }, BASE_PAGES[3]]
    : BASE_PAGES;

  function openNoteModal(client) {
    setNoteClient(client);
    setNoteForm(emptyNote);
    setNoteError("");
    setNoteSuccess("");
    setNoteModal(true);
  }

  async function sendNote() {
    if (!noteForm.message.trim()) return setNoteError(t("vendor_note_enter_msg"));
    setNoteSaving(true);
    setNoteError("");
    try {
      const clientName = noteClient
        ? (noteClient.name || `${noteClient.first_name || ""} ${noteClient.last_name || ""}`.trim())
        : "General";
      const notePayload = {
        client_id:       noteClient?.id    || null,
        client_name:     clientName,
        client_phone:    noteClient?.phone || "",
        client_location: noteClient?.location || null,
        message:         noteForm.message.trim(),
        priority:        noteForm.priority,
        sent_by:         currentUser.uid,
        sent_by_name:    currentUser.displayName || currentUser.email,
        sent_by_role:    "vendor",
        created_at:      new Date().toISOString(),
        read:            false,
      };
      if (!navigator.onLine) {
        await savePendingNote({ note: notePayload });
        setPendingCount(c => c + 1);
        setNoteSuccess(t("vendor_note_saved_offline"));
      } else {
        await addDoc(collection(db, "companies", companyId, "notes"), {
          ...notePayload,
          created_at: serverTimestamp(),
        });
        setNoteSuccess(t("vendor_note_sent"));
      }
      setTimeout(() => { setNoteModal(false); setNoteClient(null); setNoteSuccess(""); }, 1500);
    } catch (err) {
      setNoteError("Error: " + err.message);
    }
    setNoteSaving(false);
  }

  function toggleDarkMode() {
    const next = !isDarkMode;
    setIsDarkMode(next);
    localStorage.setItem("pinpoint_dark_mode", String(next));
    if (next) { document.documentElement.classList.add("dark"); }
    else       { document.documentElement.classList.remove("dark"); }
  }

  // Apply on mount
  React.useEffect(() => {
    if (isDarkMode) document.documentElement.classList.add("dark");
    else            document.documentElement.classList.remove("dark");
  }, []); // eslint-disable-line

  if (stockLoading) return (
    <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? "bg-gray-900" : "bg-gray-50"}`}>
      <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
    </div>
  );

  return (
    <div className={`min-h-screen flex flex-col ${isDarkMode ? "bg-gray-900" : "bg-gray-50"}`}>

      {/* Top navbar */}
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-40 shadow-sm">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {branding.logo_url ? (
              <img src={branding.logo_url} alt={branding.platform_name} className="w-6 h-6 object-contain rounded" />
            ) : (
              <Truck className="w-5 h-5 text-blue-600" />
            )}
            <span className="font-bold text-gray-800 text-base">{(branding.platform_name || "Pinpoint").toUpperCase()}</span>
            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">{t("vendor_badge")}</span>
          </div>
          <div className="flex items-center gap-3">
            {vendorProfile && (
              <span className="text-sm text-gray-500 hidden sm:block">
                {vendorProfile.first_name} {vendorProfile.last_name}
              </span>
            )}
            <button onClick={logout}
              className="flex items-center gap-1.5 text-sm text-red-500 font-medium py-1.5 px-3 rounded-lg active:bg-red-50">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </nav>

      {/* Offline banner */}
      {!isOnline && (
        <div className="bg-orange-500 text-white px-4 py-2 flex items-center gap-2 text-sm font-medium">
          <WifiOff className="w-4 h-4 shrink-0" />
          {t("vendor_offline_banner")}
        </div>
      )}

      {/* Sync banner */}
      {isOnline && pendingCount > 0 && (
        <div className="bg-blue-600 text-white px-4 py-2 flex items-center justify-between gap-2 text-sm">
          <span className="flex items-center gap-2">
            <RefreshCw className="w-4 h-4 shrink-0" />
            {pendingCount} {t("vendor_pending_sync")}
          </span>
          <button onClick={handleSync} disabled={syncing}
            className="bg-white text-blue-600 font-bold px-3 py-1 rounded-lg text-xs disabled:opacity-60 flex items-center gap-1">
            {syncing ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
            {syncing ? t("vendor_syncing") : t("vendor_sync_now")}
          </button>
        </div>
      )}

      {/* Sync success */}
      {syncSuccess && (
        <div className="bg-green-500 text-white px-4 py-2 flex items-center gap-2 text-sm font-medium">
          <CheckCircle className="w-4 h-4 shrink-0" />{syncSuccess}
        </div>
      )}

      {/* Page content */}
      <main className={`flex-1 ${activePage === "map" ? "overflow-hidden" : "pb-24 overflow-y-auto"}`}>
        {activePage === "route"   && (
          <RoutePage
            companyId={companyId} currentUser={currentUser}
            stockEnabled={stockEnabled}
            vendorProfile={vendorProfile}
            vendorDocId={vendorDocId}
            nearbyMin={nearbyMin}
            nearbyMax={nearbyMax}
            allowManualCheckin={allowManualCheckin}
            requireNotesOnVisit={requireNotesOnVisit}
            routeOptimization={routeOptimization}
            companyTimezone={companyTimezone}
            onSendNote={openNoteModal}
            onSellItem={client => { setSellClient(client); setSellModal(true); }}
          />
        )}
        {activePage === "map"     && (
          <MapPage
            companyId={companyId} currentUser={currentUser}
            stockEnabled={stockEnabled}
            vendorProfile={vendorProfile}
            visitResetDays={visitResetDays}
            onSendNote={openNoteModal}
            onSellItem={client => { setSellClient(client); setSellModal(true); }}
          />
        )}
        {activePage === "stats"   && <StatsPage   companyId={companyId} currentUser={currentUser} vendorDocId={vendorDocId} companyTimezone={companyTimezone} />}
        {activePage === "stock"   && stockEnabled && <StockPage companyId={companyId} currentUser={currentUser} vendorProfile={vendorProfile} companyTimezone={companyTimezone} />}
        {activePage === "profile" && (
          <ProfilePage
            companyId={companyId} currentUser={currentUser}
            vendorProfile={vendorProfile}
            onSendNote={() => openNoteModal(null)}
            isOnline={isOnline}
            pendingCount={pendingCount}
            lastSyncTime={lastSyncTime}
            onSync={handleSync}
            syncing={syncing}
            isDarkMode={isDarkMode}
            toggleDarkMode={toggleDarkMode}
          />
        )}
      </main>

      {/* Bottom tab bar */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-40">
        <div className="flex">
          {pages.map(({ id, Icon, labelKey }) => {
            const active = activePage === id;
            return (
              <button key={id} onClick={() => setActivePage(id)}
                className={`flex-1 flex flex-col items-center justify-center py-3 gap-1 transition-colors ${
                  active ? "text-blue-600" : "text-gray-400 active:bg-gray-50"
                }`}>
                <Icon className="w-5 h-5" />
                <span className="text-[10px] font-medium leading-none">{t(labelKey)}</span>
                {active && <span className="w-1 h-1 rounded-full bg-blue-500" />}
              </button>
            );
          })}
        </div>
      </nav>

      {/* ── Send Note Modal ─────────────────────────────────────── */}
      {noteModal && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setNoteModal(false)} />
          <div className="relative bg-white rounded-t-3xl sm:rounded-2xl w-full sm:max-w-sm shadow-2xl">
            <div className="flex items-center justify-between px-5 pt-5 pb-3">
              <div className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-purple-600" />
                <h2 className="text-base font-bold text-gray-800">{t("vendor_send_note_title")}</h2>
              </div>
              <button onClick={() => setNoteModal(false)}
                className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>
            <div className="px-5 pb-6 space-y-4">
              <div className="bg-gray-50 rounded-xl px-4 py-3 text-sm text-gray-600 font-medium">
                {noteClient
                  ? (noteClient.name || `${noteClient.first_name || ""} ${noteClient.last_name || ""}`.trim())
                  : t("vendor_general_note")}
              </div>
              {noteError && (
                <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-3 flex items-center gap-2 text-sm">
                  <AlertTriangle className="w-4 h-4 shrink-0" />{noteError}
                </div>
              )}
              {noteSuccess && (
                <div className="bg-green-50 border border-green-200 text-green-700 rounded-xl p-3 text-sm">
                  {noteSuccess}
                </div>
              )}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  {t("vendor_note_message")} <span className="text-red-400">*</span>
                </label>
                <textarea value={noteForm.message}
                  onChange={e => setNoteForm(p => ({ ...p, message: e.target.value }))}
                  rows={4}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 resize-none"
                  placeholder={t("vendor_note_describe_ph")} />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">{t("vendor_note_priority")}</label>
                <div className="relative">
                  <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  <select value={noteForm.priority}
                    onChange={e => setNoteForm(p => ({ ...p, priority: e.target.value }))}
                    className="w-full pr-10 border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 appearance-none bg-white">
                    <option value="normal">{t("vendor_note_normal")}</option>
                    <option value="urgent">{t("vendor_note_urgent")}</option>
                  </select>
                </div>
              </div>
              <div className="flex gap-3 pt-1">
                <button onClick={() => setNoteModal(false)}
                  className="flex-1 border border-gray-200 text-gray-600 font-medium py-3.5 rounded-2xl text-sm">
                  {t("vendor_note_cancel")}
                </button>
                <button onClick={sendNote} disabled={noteSaving}
                  className="flex-1 bg-purple-600 hover:bg-purple-700 disabled:opacity-60 text-white font-bold py-3.5 rounded-2xl flex items-center justify-center gap-2 transition text-sm">
                  {noteSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {noteSaving ? t("vendor_note_sending") : t("vendor_note_send")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Sell Item Modal ─────────────────────────────────────── */}
      {sellModal && (
        <SellItemModal
          companyId={companyId}
          currentUser={currentUser}
          vendorProfile={vendorProfile}
          client={sellClient}
          onClose={() => { setSellModal(false); setSellClient(null); }}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Route optimization helpers
// ─────────────────────────────────────────────────────────────────────────────
function optimizeRouteBasic(clients, vendorLat, vendorLng) {
  if (!clients || clients.length === 0) return { route: [], totalDistance: 0 };

  const unvisited  = [...clients];
  const route      = [];
  let currentLat   = vendorLat;
  let currentLng   = vendorLng;
  let totalDistance = 0;

  while (unvisited.length > 0) {
    let closestIndex    = 0;
    let closestDistance = Infinity;

    // eslint-disable-next-line no-loop-func
    unvisited.forEach((client, index) => {
      const clientLat = client.location?.lat ?? client.lat;
      const clientLng = client.location?.lng ?? client.lng;
      if (!clientLat || !clientLng) return;
      const distance = getDistance(
        { latitude: currentLat,              longitude: currentLng },
        { latitude: parseFloat(clientLat),   longitude: parseFloat(clientLng) }
      );
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex    = index;
      }
    });

    const nearest = unvisited[closestIndex];
    route.push({ ...nearest, distanceFromPrev: Math.round(closestDistance) });
    totalDistance += closestDistance;

    const nearestLat = nearest.location?.lat ?? nearest.lat;
    const nearestLng = nearest.location?.lng ?? nearest.lng;
    currentLat = parseFloat(nearestLat);
    currentLng = parseFloat(nearestLng);
    unvisited.splice(closestIndex, 1);
  }

  return { route, totalDistance: Math.round(totalDistance / 1000 * 10) / 10 };
}

async function optimizeRouteGoogle(clients, vendorLat, vendorLng) {
  if (!clients || clients.length === 0) return { route: [], totalDistance: 0 };

  const waypoints = clients
    .filter(c => {
      const lat = c.location?.lat ?? c.lat;
      const lng = c.location?.lng ?? c.lng;
      return lat && lng;
    })
    .map(c => ({
      client: c,
      lat: parseFloat(c.location?.lat ?? c.lat),
      lng: parseFloat(c.location?.lng ?? c.lng),
    }));

  if (waypoints.length === 0) return { route: clients, totalDistance: 0 };

  // Google Routes API allows max 25 waypoints — fall back to basic if more
  if (waypoints.length > 25) {
    return optimizeRouteBasic(clients, vendorLat, vendorLng);
  }

  try {
    const response = await fetch(
      "https://routes.googleapis.com/directions/v2:computeRoutes",
      {
        method: "POST",
        headers: {
          "Content-Type":      "application/json",
          "X-Goog-Api-Key":    "AIzaSyCnYs5O0ln5DWlshgPWPy5FJhNmneDHCtk",
          "X-Goog-FieldMask":  "routes.duration,routes.distanceMeters,routes.legs,routes.optimized_intermediate_waypoint_index",
        },
        body: JSON.stringify({
          origin: {
            location: { latLng: { latitude: vendorLat, longitude: vendorLng } }
          },
          destination: {
            location: { latLng: {
              latitude:  waypoints[waypoints.length - 1].lat,
              longitude: waypoints[waypoints.length - 1].lng,
            }}
          },
          intermediates: waypoints.slice(0, -1).map(wp => ({
            location: { latLng: { latitude: wp.lat, longitude: wp.lng } }
          })),
          travelMode:            "DRIVE",
          optimizeWaypointOrder: true,
          routingPreference:     "TRAFFIC_AWARE",
        }),
      }
    );

    if (!response.ok) {
      return optimizeRouteBasic(clients, vendorLat, vendorLng);
    }

    const data  = await response.json();
    const route = data.routes?.[0];
    if (!route) return optimizeRouteBasic(clients, vendorLat, vendorLng);

    const optimizedOrder    = route.optimized_intermediate_waypoint_index || [];
    const reorderedClients  = optimizedOrder.map(i => waypoints[i]?.client).filter(Boolean);
    reorderedClients.push(waypoints[waypoints.length - 1].client);

    const totalDistance = Math.round((route.distanceMeters || 0) / 100) / 10;
    const totalDuration = Math.round((parseInt(route.duration) || 0) / 60);

    return { route: reorderedClients, totalDistance, totalDuration };

  } catch (err) {
    return optimizeRouteBasic(clients, vendorLat, vendorLng);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// PAGE 1 — Today's Route
// ─────────────────────────────────────────────────────────────────────────────
function RoutePage({ companyId, currentUser, stockEnabled, vendorProfile, vendorDocId, nearbyMin, nearbyMax, allowManualCheckin, requireNotesOnVisit, routeOptimization, companyTimezone = "Asia/Dubai", onSendNote, onSellItem }) {
  const { t } = useLanguage();
  const [clients,        setClients]        = useState([]);
  const [noZoneAssigned, setNoZoneAssigned] = useState(false);
  const [sections,       setSections]       = useState([]);
  const [receiptSale,    setReceiptSale]    = useState(null);   // sale shown in receipt modal
  const [fetchingReceipt, setFetchingReceipt] = useState(null); // clientId being fetched

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });
  const [loading,        setLoading]        = useState(true);
  const [error,          setError]          = useState("");
  const [filter,         setFilter]         = useState("all");
  const [search,         setSearch]         = useState("");
  // nearbyDist removed — using nearbyMax prop directly (read from config/settings)
  const [actionStates,   setActionStates]   = useState({}); // { [clientId]: { checking } }
  const [vendorPos,      setVendorPos]      = useState(null); // vendor's current GPS
  const [clientDists,    setClientDists]    = useState({}); // { [clientId]: meters }
  const [locating,       setLocating]       = useState(false);
  const [geoError,       setGeoError]       = useState(""); // geolocation denial/error message
  const [manualPinMode,  setManualPinMode]  = useState(false); // fallback manual pin on map
  const [autoToast,          setAutoToast]          = useState(""); // auto check-in toast message
  const [checkinNoteModal,   setCheckinNoteModal]   = useState(false); // require_notes gate modal
  const [checkinNoteClient,  setCheckinNoteClient]  = useState(null);  // client pending check-in
  const [checkinNote,        setCheckinNote]        = useState("");     // note text
  const [checkinNoteError,   setCheckinNoteError]   = useState("");
  const checkedInToday   = useRef(new Set()); // prevent duplicate auto check-ins

  // Route optimization state
  const [optimizedClients, setOptimizedClients] = useState([]);
  const [isOptimized,      setIsOptimized]      = useState(false);
  const [optimizing,       setOptimizing]       = useState(false);
  const [optimizeError,    setOptimizeError]    = useState("");
  const [routeSummary,     setRouteSummary]     = useState(null);

  useEffect(() => {
    if (!companyId || !currentUser?.uid) return;
    loadData();
    refreshLocation();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, vendorProfile, vendorDocId, currentUser?.uid]); // re-run when profile loads so zone filter applies

  function refreshLocation() {
    if (!navigator.geolocation) {
      setGeoError("Geolocation is not supported by your browser.");
      return;
    }
    setLocating(true);
    setGeoError("");
    navigator.geolocation.getCurrentPosition(
      pos => {
        setVendorPos({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setGeoError("");
        setManualPinMode(false);
        setLocating(false);
      },
      (err) => {
        setLocating(false);
        if (err.code === 1) {
          setGeoError("Location access was denied. To use Check In you must allow location access. On Chrome: tap the lock icon in the address bar → Site settings → Location → Allow. Then refresh the page.");
          setManualPinMode(true);
        } else if (err.code === 2) {
          setGeoError("Your location could not be determined. Please check your GPS signal and try again.");
        } else if (err.code === 3) {
          setGeoError("Location request timed out. Please try again.");
        } else {
          setGeoError("Could not get location: " + err.message);
        }
      },
      { timeout: 10000 }
    );
  }

  // Recalculate distances to each client when vendor GPS updates
  useEffect(() => {
    if (!vendorPos) return;
    const dists = {};
    clients.forEach(c => {
      const clat = c.location?.lat ?? c.location?.latitude;
      const clng = c.location?.lng ?? c.location?.longitude;
      if (clat && clng) {
        dists[c.id] = getDistance(
          { latitude: vendorPos.lat, longitude: vendorPos.lng },
          { latitude: clat, longitude: clng }
        );
      }
    });
    setClientDists(dists);
  }, [vendorPos, clients]);

  // Auto check-in: watch vendor's Firestore location doc for speed===0 + nearby client
  useEffect(() => {
    if (!companyId || !currentUser) return;
    const unsub = onSnapshot(
      doc(db, "companies", companyId, "users", vendorDocId || currentUser.uid),
      async (snap) => {
        if (!snap.exists()) return;
        const data = snap.data();
        const lat  = data.lat;
        const lng  = data.lng;
        const speed = data.speed ?? 1; // default non-zero to avoid false trigger
        if (!lat || !lng || speed !== 0) return;

        // Check each client for proximity
        const todayStr = todayStringInTimezone(companyTimezone);
        for (const client of clients) {
          const clat = client.location?.lat ?? client.location?.latitude;
          const clng = client.location?.lng ?? client.location?.longitude;
          if (!clat || !clng) continue;
          const dist = getDistance(
            { latitude: lat, longitude: lng },
            { latitude: clat, longitude: clng }
          );
          if (dist > nearbyMax) continue;

          // Skip if already checked in today (compare in company timezone)
          const key = `${client.id}_${todayStr}`;
          if (checkedInToday.current.has(key)) continue;
          if (client.last_visited_at) {
            const visitDate = client.last_visited_at.toDate
              ? client.last_visited_at.toDate()
              : new Date(client.last_visited_at);
            if (visitDate.toLocaleDateString("en-CA", { timeZone: companyTimezone }) === todayStr) continue;
          }

          checkedInToday.current.add(key);
          const clientName = client.name || `${client.first_name || ""} ${client.last_name || ""}`.trim();
          const vendorName = vendorProfile
            ? `${vendorProfile.first_name || ""} ${vendorProfile.last_name || ""}`.trim() || currentUser.email
            : (currentUser.displayName || currentUser.email);

          try {
            await Promise.all([
              addDoc(collection(db, "companies", companyId, "events"), {
                type:                 "checkin",
                auto:                 true,
                vendor_id:            currentUser.uid,
                vendor_name:          vendorName,
                client_id:            client.id,
                client_name:          clientName,
                timestamp:            serverTimestamp(),
                lat,
                lng,
                distance_from_client: dist,
              }),
              updateDoc(doc(db, "companies", companyId, "clients", client.id), {
                last_visited_by: currentUser.uid,
                last_visited_at: serverTimestamp(),
                last_status:     "arrived",
              }),
            ]);
            // Increment visit counters — same as manual check-in
            try {
              await updateDoc(doc(db, "companies", companyId), {
                visits_today: increment(1),
                visits_week:  increment(1),
              });
            } catch (e) { console.error("[COUNTER] visits_today increment failed:", e.message); }
            setClients(prev => prev.map(c =>
              c.id === client.id ? { ...c, last_status: "arrived" } : c
            ));
            setAutoToast(`Auto checked in at ${clientName}`);
            setTimeout(() => setAutoToast(""), 4000);
          } catch (err) {
            console.error("auto check-in error:", err.message);
          }
          break; // only auto check-in one client per location update
        }
      }
    );
    return () => unsub();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, currentUser, clients, nearbyMax]);

  async function loadData() {
    setLoading(true);
    try {
      if (!navigator.onLine) {
        // Offline — serve from IndexedDB cache
        const [cachedClients, cachedSections] = await Promise.all([
          getClientsOffline(),
          getSectionsOffline(),
        ]);
        if (cachedClients.length > 0) {
          const assignedSections = vendorProfile
            ? (Array.isArray(vendorProfile.assigned_sections)
                ? vendorProfile.assigned_sections
                : Array.isArray(vendorProfile.sections)
                  ? vendorProfile.sections
                  : vendorProfile.assigned_section
                    ? [vendorProfile.assigned_section]
                    : [])
            : [];
          const assigned = cachedClients.filter(c => {
            if (c.assigned_vendor === currentUser.uid) return true;
            if (assignedSections.length > 0 && c.section && assignedSections.includes(c.section)) return true;
            return false;
          });
          setNoZoneAssigned(assignedSections.length === 0 && !vendorProfile?.assigned_vendor);
          setClients(assigned);
          setSections(cachedSections);
          setLoading(false);
          return;
        }
        throw new Error("No cached data available offline.");
      }

      const [clientsSnap, sectionsSnap] = await Promise.all([
        getDocs(collection(db, "companies", companyId, "clients")),
        getDocs(collection(db, "companies", companyId, "sections")),
      ]);
      const allClients = clientsSnap.docs.map(d => ({ id: d.id, ...d.data() }));

      // Resolve vendor's assigned sections — check all possible field names
      const assignedSections = vendorProfile
        ? (Array.isArray(vendorProfile.assigned_sections)
            ? vendorProfile.assigned_sections          // plural array field
            : Array.isArray(vendorProfile.sections)
              ? vendorProfile.sections                 // sections array field
              : vendorProfile.assigned_section
                ? [vendorProfile.assigned_section]     // singular string field
                : [])
        : [];

      const assigned = allClients.filter(c => {
        // Client explicitly assigned to this vendor
        if (c.assigned_vendor === currentUser.uid) return true;
        // Client in vendor's assigned section(s)
        if (assignedSections.length > 0 && c.section && assignedSections.includes(c.section)) return true;
        // No fallback — if vendor has no section assignment, show nothing
        return false;
      });
      setNoZoneAssigned(assignedSections.length === 0 && !vendorProfile?.assigned_vendor);
      setClients(assigned);
      const sectionList = sectionsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      setSections(sectionList);
      console.log("[RoutePage] loadData — nearbyMin prop:", nearbyMin, "nearbyMax prop:", nearbyMax);
      // Cache for offline use
      saveClientsOffline(allClients).catch(() => {});
      saveSectionsOffline(sectionList).catch(() => {});
    } catch (err) {
      setError("Error loading data: " + err.message);
    } finally {
      setLoading(false);
    }
  }

  function setActionState(clientId, key, val) {
    setActionStates(prev => ({
      ...prev,
      [clientId]: { ...(prev[clientId] || {}), [key]: val },
    }));
  }

  async function fetchAndShowReceipt(client) {
    setFetchingReceipt(client.id);
    setError("");
    try {
      const snap = await getDocs(query(
        collection(db, "companies", companyId, "sales"),
        where("vendor_id", "==", currentUser.uid),
        where("client_id", "==", client.id),
        limit(10)
      ));
      const allSales = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      // Sort newest first
      allSales.sort((a, b) => {
        const ta = a.created_at?.toDate ? a.created_at.toDate() : new Date(a.created_at || 0);
        const tb = b.created_at?.toDate ? b.created_at.toDate() : new Date(b.created_at || 0);
        return tb - ta;
      });
      // Prefer today's sale, fall back to most recent
      const todayStr  = todayStringInTimezone(companyTimezone);
      const todaySale = allSales.find(s => {
        const d = s.created_at?.toDate ? s.created_at.toDate() : new Date(s.created_at || 0);
        return d.toLocaleDateString("en-CA", { timeZone: companyTimezone }) === todayStr;
      });
      const sale = todaySale || allSales[0];
      if (sale) {
        setReceiptSale(sale);
      } else {
        setError("No receipt found for this client.");
      }
    } catch (err) {
      setError("Error: " + err.message + (err.code ? ` (${err.code})` : ""));
    }
    setFetchingReceipt(null);
  }

  // Gate function — opens note modal if required, else checks in directly
  function handleCheckinClick(client) {
    if (requireNotesOnVisit) {
      setCheckinNoteClient(client);
      setCheckinNote("");
      setCheckinNoteError("");
      setCheckinNoteModal(true);
    } else {
      handleCheckIn(client, "");
    }
  }

  async function handleCheckIn(client, noteText = "") {
    setActionState(client.id, "checking", true);
    setError("");
    try {
      let vendorLat, vendorLng;

      if (vendorPos) {
        // Use already-known position (GPS or manual pin)
        vendorLat = vendorPos.lat;
        vendorLng = vendorPos.lng;
      } else {
        // Request GPS fresh
        const pos = await new Promise((res, rej) =>
          navigator.geolocation.getCurrentPosition(res, rej, { timeout: 10000 })
        ).catch(err => {
          if (err.code === 1) throw new Error("Location access was denied. Tap the lock icon in your browser address bar → Site settings → Location → Allow, then try again.");
          if (err.code === 2) throw new Error("Your location could not be determined. Check your GPS signal and try again.");
          if (err.code === 3) throw new Error("Location request timed out. Please try again.");
          throw new Error("Could not get location: " + err.message);
        });
        vendorLat = pos.coords.latitude;
        vendorLng = pos.coords.longitude;
        setVendorPos({ lat: vendorLat, lng: vendorLng });
      }
      const clientLat = client.location?.lat ?? client.location?.latitude;
      const clientLng = client.location?.lng ?? client.location?.longitude;

      if (!clientLat || !clientLng) {
        throw new Error("This client has no GPS location set.");
      }

      const distMeters = getDistance(
        { latitude: vendorLat, longitude: vendorLng },
        { latitude: clientLat, longitude: clientLng }
      );

      console.log("[handleCheckIn] dist:", distMeters, "m — nearbyMax:", nearbyMax, "m");
      if (distMeters > nearbyMax) {
        throw new Error(`You are ${distMeters}m away — must be within ${nearbyMax}m to check in.`);
      }

      const vendorName = vendorProfile
        ? `${vendorProfile.first_name || ""} ${vendorProfile.last_name || ""}`.trim() || currentUser.email
        : (currentUser.displayName || currentUser.email);
      const clientName = client.name || `${client.first_name || ""} ${client.last_name || ""}`.trim();

      // Offline — queue the check-in and update local state immediately
      if (!navigator.onLine) {
        await savePendingCheckin({
          clientId:  client.id,
          vendorId:  currentUser.uid,
          newStatus: "arrived",
          event: {
            type:                 "checkin",
            vendor_id:            currentUser.uid,
            vendor_name:          vendorName,
            client_id:            client.id,
            client_name:          clientName,
            lat:                  vendorLat,
            lng:                  vendorLng,
            distance_from_client: distMeters,
          },
        });
        setClients(prev => prev.map(c =>
          c.id === client.id ? { ...c, last_status: "arrived", _offlineCheckin: true } : c
        ));
        setActionState(client.id, "checking", false);
        return;
      }

      const writes = [
        addDoc(collection(db, "companies", companyId, "events"), {
          type:                "checkin",
          vendor_id:           currentUser.uid,
          vendor_name:         vendorName,
          client_id:           client.id,
          client_name:         clientName,
          timestamp:           serverTimestamp(),
          lat:                 vendorLat,
          lng:                 vendorLng,
          distance_from_client: distMeters,
        }),
        updateDoc(doc(db, "companies", companyId, "clients", client.id), {
          last_visited_by:  currentUser.uid,
          last_visited_at:  serverTimestamp(),
          last_status:      "arrived",
        }),
      ];
      // Save required note if provided
      if (noteText && noteText.trim()) {
        writes.push(addDoc(collection(db, "companies", companyId, "notes"), {
          client_id:       client.id,
          client_name:     clientName,
          client_phone:    client.phone || "",
          message:         noteText.trim(),
          priority:        "normal",
          sent_by:         currentUser.uid,
          sent_by_name:    vendorName,
          sent_by_role:    "vendor",
          type:            "checkin_note",
          created_at:      serverTimestamp(),
          read:            false,
        }));
      }
      await Promise.all(writes);

      // Increment visits_today counter on company doc
      try {
        await updateDoc(doc(db, "companies", companyId), {
          visits_today: increment(1),
          visits_week:  increment(1),
        });
      } catch (e) { console.error("[COUNTER] visits_today increment failed:", e.message); }

      setClients(prev => prev.map(c =>
        c.id === client.id ? { ...c, last_status: "arrived" } : c
      ));
    } catch (err) {
      setError(err.message);
    }
    setActionState(client.id, "checking", false);
  }

  function navigateTo(client) {
    const loc = client.location;
    const navLat = loc?.lat ?? loc?.latitude;
    const navLng = loc?.lng ?? loc?.longitude;
    if (navLat && navLng) {
      window.open(`https://www.google.com/maps/dir/?api=1&destination=${navLat},${navLng}`, "_blank");
    } else if (client.address) {
      window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(client.address)}`, "_blank");
    }
  }

  function getStatusLabel(c) {
    const s = c.last_status;
    if (s === "arrived") return { label: t("vendor_route_visited_label"), color: "bg-green-100 text-green-700" };
    return                      { label: t("vendor_route_pending"),       color: "bg-yellow-100 text-yellow-700" };
  }

  function getSectionName(id) {
    return sections.find(s => s.id === id)?.name || id || "—";
  }

  async function handleOptimizeRoute() {
    if (optimizing) return;
    setOptimizing(true);
    setOptimizeError("");
    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
        });
      });
      const vendorLat = position.coords.latitude;
      const vendorLng = position.coords.longitude;

      const clientsToOptimize = filtered.filter(c => c.last_status !== "done");
      if (clientsToOptimize.length < 2) {
        setOptimizeError("Need at least 2 remaining clients to optimize route.");
        setOptimizing(false);
        return;
      }

      let result;
      if (routeOptimization === "google") {
        result = await optimizeRouteGoogle(clientsToOptimize, vendorLat, vendorLng);
      } else {
        result = optimizeRouteBasic(clientsToOptimize, vendorLat, vendorLng);
      }

      // Calculate original order distance for savings comparison
      let originalDistance = 0;
      let prevLat = vendorLat;
      let prevLng = vendorLng;
      clientsToOptimize.forEach(c => {
        const lat = parseFloat(c.location?.lat ?? c.lat);
        const lng = parseFloat(c.location?.lng ?? c.lng);
        if (lat && lng) {
          originalDistance += getDistance(
            { latitude: prevLat, longitude: prevLng },
            { latitude: lat,     longitude: lng }
          );
          prevLat = lat;
          prevLng = lng;
        }
      });
      const originalKm = Math.round(originalDistance / 100) / 10;
      const savings    = Math.max(0, Math.round((originalKm - result.totalDistance) * 10) / 10);

      setOptimizedClients(result.route);
      setIsOptimized(true);
      setRouteSummary({
        totalDistance: result.totalDistance,
        estimatedTime: result.totalDuration || Math.round(result.totalDistance * 3),
        savings,
        mode: routeOptimization,
      });
    } catch (err) {
      if (err.code === 1) {
        setOptimizeError("Location access denied. Please enable location to optimize route.");
      } else {
        setOptimizeError(`Could not optimize route: ${err.message}`);
      }
    }
    setOptimizing(false);
  }

  function handleResetRoute() {
    setOptimizedClients([]);
    setIsOptimized(false);
    setRouteSummary(null);
    setOptimizeError("");
  }

  // Filter clients to only show those 20–100m away when vendor pos is known
  const MIN_DIST = nearbyMin ?? 20;
  const MAX_DIST = nearbyMax ?? 100;

  // Group by section
  const filtered = clients.filter(c => {
    // While GPS is loading (no position yet and no error), don't show any clients
    if (!vendorPos && !geoError) return false;
    // Once GPS is available, apply distance filter
    if (vendorPos) {
      const d = clientDists[c.id];
      if (d == null) return false;         // client has no GPS — hide
      if (d < MIN_DIST || d > MAX_DIST) return false; // outside range
    }
    // GPS denied/error + no vendorPos → show all zone clients (fallback, manual pin mode)
    const matchFilter =
      filter === "all"  ||
      (filter === "visited" && c.last_status === "arrived") ||
      (filter === "pending" && c.last_status !== "arrived");
    const matchSearch = !search || (
      `${c.first_name} ${c.last_name} ${c.name} ${c.phone} ${c.address}`
        .toLowerCase().includes(search.toLowerCase())
    );
    return matchFilter && matchSearch;
  });

  const grouped = {};
  filtered.forEach(c => {
    const key = c.section || "__none__";
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(c);
  });

  const totalClients  = clients.length;
  const visitedCount  = clients.filter(c => c.last_status === "arrived").length;
  const remaining     = totalClients - visitedCount;

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
    </div>
  );

  return (
    <div className="p-4 space-y-4">

      {/* Summary bar */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white rounded-xl p-3 text-center shadow-sm">
          <p className="text-2xl font-bold text-gray-800">{totalClients}</p>
          <p className="text-xs text-gray-500 mt-0.5">{t("vendor_route_total")}</p>
        </div>
        <div className="bg-green-50 rounded-xl p-3 text-center shadow-sm">
          <p className="text-2xl font-bold text-green-700">{visitedCount}</p>
          <p className="text-xs text-green-600 mt-0.5">{t("vendor_route_visited")}</p>
        </div>
        <div className="bg-yellow-50 rounded-xl p-3 text-center shadow-sm">
          <p className="text-2xl font-bold text-yellow-700">{remaining}</p>
          <p className="text-xs text-yellow-600 mt-0.5">{t("vendor_route_remaining")}</p>
        </div>
      </div>

      {/* Auto check-in toast */}
      {autoToast && (
        <div className="bg-green-600 text-white rounded-xl p-3 flex items-center gap-2 text-sm font-medium shadow-lg">
          <CheckCircle className="w-4 h-4 shrink-0" />{autoToast}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-3 flex items-center gap-2 text-sm">
          <AlertTriangle className="w-4 h-4 shrink-0" />{error}
          <button onClick={() => setError("")} className="ml-auto"><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* Nearby info + Refresh button */}
      <div className="flex items-center justify-between bg-white rounded-xl px-4 py-3 shadow-sm">
        <div className="text-sm text-gray-500">
          {vendorPos
            ? <span className="text-green-600 font-medium flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {t("vendor_route_showing_clients")} {MIN_DIST}–{MAX_DIST}m {t("vendor_route_away")} ({filtered.length} {t("vendor_route_found")})</span>
            : <span className="text-gray-400">{t("vendor_route_getting_location")}</span>
          }
        </div>
        <div className="flex items-center gap-2">
          <button onClick={refreshLocation} disabled={locating}
            className="flex items-center gap-1.5 text-xs font-semibold text-blue-600 bg-blue-50 border border-blue-200 px-3 py-1.5 rounded-lg disabled:opacity-60 active:bg-blue-100">
            {locating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Navigation className="w-3.5 h-3.5" />}
            {locating ? t("vendor_route_locating") : t("vendor_route_refresh")}
          </button>
        </div>
      </div>

      {/* Geolocation error / permission denied banner */}
      {geoError && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm space-y-2">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <p>{geoError}</p>
          </div>
          {manualPinMode && (
            <div className="mt-2">
              <p className="text-xs text-red-600 font-semibold mb-2">Or tap your location on the map below to check in manually:</p>
              <div style={{ height: 200 }} className="rounded-xl overflow-hidden border border-red-200">
                {isLoaded ? (
                  <GoogleMap
                    mapContainerStyle={{ width: "100%", height: "100%" }}
                    center={DEFAULT_CENTER}
                    zoom={14}
                    onClick={e => {
                      const p = { lat: e.latLng.lat(), lng: e.latLng.lng() };
                      setVendorPos(p);
                      setGeoError("");
                      setManualPinMode(false);
                    }}
                    options={{ streetViewControl: false, mapTypeControl: false, fullscreenControl: false }}
                  >
                    {vendorPos && <Marker position={vendorPos} />}
                  </GoogleMap>
                ) : (
                  <div className="h-full flex items-center justify-center bg-gray-100">
                    <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Route optimization card */}
      {routeOptimization !== "disabled" && (
        <div className={`rounded-xl p-4 border-2 ${isOptimized ? "bg-blue-50 border-blue-200" : "bg-white border-gray-200"}`}>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isOptimized ? "bg-blue-500" : "bg-gray-100"}`}>
                <Navigation className={`w-5 h-5 ${isOptimized ? "text-white" : "text-gray-500"}`} />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-800">
                  {isOptimized ? t("vendor_route_optimized") : t("vendor_route_optimization")}
                </p>
                {isOptimized && routeSummary ? (
                  <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                    <span className="text-xs text-blue-600 font-medium">{routeSummary.totalDistance} {t("vendor_route_km")} · ~{routeSummary.estimatedTime} {t("vendor_route_min")}</span>
                    {routeSummary.savings > 0 && (
                      <span className="text-xs text-green-600 font-medium">{t("vendor_route_saves")} {routeSummary.savings} {t("vendor_route_km")}</span>
                    )}
                    {routeSummary.mode === "google" && (
                      <span className="text-xs bg-blue-500 text-white px-1.5 py-0.5 rounded-full font-bold">Google</span>
                    )}
                  </div>
                ) : (
                  <p className="text-xs text-gray-400 mt-0.5">
                    {routeOptimization === "google" ? "Google Routes — real driving directions" : "Smart ordering — shortest path"}
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={isOptimized ? handleResetRoute : handleOptimizeRoute}
              disabled={optimizing || filtered.length < 2}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition disabled:opacity-50 ${
                isOptimized
                  ? "bg-gray-200 hover:bg-gray-300 text-gray-700"
                  : routeOptimization === "google"
                    ? "bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white shadow-lg shadow-blue-200"
                    : "bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-white shadow-lg shadow-yellow-200"
              }`}>
              {optimizing ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> {t("vendor_route_optimizing")}</>
              ) : isOptimized ? (
                <><RefreshCw className="w-4 h-4" /> {t("vendor_route_reset_order")}</>
              ) : (
                <>
                  <Navigation className="w-4 h-4" />
                  {t("vendor_route_optimize")}
                  {routeOptimization === "google" && (
                    <span className="text-xs bg-white text-blue-600 px-1.5 py-0.5 rounded font-bold ml-1">PRO</span>
                  )}
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {optimizeError && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-3">
          <p className="text-red-700 text-sm">{optimizeError}</p>
        </div>
      )}

      {/* Filter tabs */}
      <div className="flex gap-2">
        {[
          { key: "all",     label: t("vendor_route_all")           },
          { key: "pending", label: t("vendor_route_pending")       },
          { key: "visited", label: t("vendor_route_visited_label") },
        ].map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)}
            className={`flex-1 py-2 rounded-xl text-sm font-semibold transition ${
              filter === f.key
                ? "bg-blue-600 text-white"
                : "bg-white text-gray-600 border border-gray-200"
            }`}>
            {f.label}
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input type="text" value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder={t("vendor_route_search_ph")}
          className="w-full pl-9 border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
      </div>

      {/* Client groups */}
      {noZoneAssigned && (
        <div className="bg-orange-50 border border-orange-200 text-orange-700 rounded-xl p-4 text-sm flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <p>{t("vendor_route_no_zone")}</p>
        </div>
      )}

      {filtered.length === 0 && !noZoneAssigned && (
        <div className="text-center py-10 text-gray-400 text-sm">
          {vendorPos
            ? t("vendor_route_no_nearby")
            : t("vendor_route_no_clients")}
        </div>
      )}

      {(isOptimized
        ? [["__optimized__", optimizedClients]]
        : Object.entries(grouped)
      ).map(([sectionId, sectionClients]) => (
        <div key={sectionId}>
          {!isOptimized && (
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-2 flex items-center gap-1">
              <MapPin className="w-3 h-3" />
              {sectionId === "__none__" ? t("vendor_route_no_zone_label") : getSectionName(sectionId)}
              <span className="ml-1 bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-full text-[10px]">
                {sectionClients.length}
              </span>
            </p>
          )}
          <div className="space-y-3">
            {sectionClients.map((client, clientIndex) => {
              const st    = getStatusLabel(client);
              const state = actionStates[client.id] || {};
              const clientName = client.name || `${client.first_name || ""} ${client.last_name || ""}`.trim();
              return (
                <div key={client.id}
                  className="bg-white rounded-2xl shadow-sm p-4 space-y-3">
                  {/* Optimized stop number + distance from previous */}
                  {isOptimized && (
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-blue-500 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
                        {clientIndex + 1}
                      </div>
                      {client.distanceFromPrev != null && (
                        <span className="text-xs text-blue-500 flex items-center gap-1">
                          <Navigation className="w-3 h-3" />
                          {client.distanceFromPrev >= 1000
                            ? `${(client.distanceFromPrev / 1000).toFixed(1)} km`
                            : `${client.distanceFromPrev} m`} {t("vendor_route_from_prev")}
                        </span>
                      )}
                    </div>
                  )}
                  {/* Client info */}
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-bold text-gray-800 text-base leading-tight">{clientName || "—"}</p>
                      {client.phone && (
                        <p className="flex items-center gap-1 text-sm text-gray-500 mt-0.5">
                          <Phone className="w-3.5 h-3.5" />{client.phone}
                        </p>
                      )}
                      {client.address && (
                        <p className="flex items-center gap-1 text-xs text-gray-400 mt-0.5">
                          <MapPin className="w-3 h-3" />{client.address}
                        </p>
                      )}
                      {/* Distance badge */}
                      {clientDists[client.id] != null && (
                        <p className="text-xs text-blue-600 font-semibold mt-1">
                          {clientDists[client.id]}m away
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <span className={`shrink-0 text-xs font-semibold px-2.5 py-1 rounded-full ${st.color}`}>
                        {st.label}
                      </span>
                      {client._offlineCheckin && (
                        <span className="text-[10px] bg-orange-100 text-orange-600 px-1.5 py-0.5 rounded-full flex items-center gap-0.5">
                          <WifiOff className="w-2.5 h-2.5" /> Offline
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div className="grid grid-cols-2 gap-2">
                    {/* Navigate */}
                    <button onClick={() => navigateTo(client)}
                      className="flex items-center justify-center gap-1.5 bg-blue-50 text-blue-700 border border-blue-200 font-semibold py-3 rounded-xl text-sm active:bg-blue-100">
                      <Navigation className="w-4 h-4" /> {t("vendor_route_navigate")}
                    </button>
                    {/* Check In — hidden when manual check-in is disabled */}
                    {allowManualCheckin !== false ? (
                      <button onClick={() => handleCheckinClick(client)} disabled={state.checking}
                        className="flex items-center justify-center gap-1.5 bg-orange-50 text-orange-700 border border-orange-200 font-semibold py-3 rounded-xl text-sm active:bg-orange-100 disabled:opacity-60">
                        {state.checking
                          ? <Loader2 className="w-4 h-4 animate-spin" />
                          : <CheckCircle className="w-4 h-4" />}
                        {t("vendor_route_check_in")}
                      </button>
                    ) : (
                      <div className="flex items-center justify-center gap-1.5 bg-gray-50 text-gray-400 border border-gray-200 font-medium py-3 rounded-xl text-xs text-center leading-tight px-2">
                        {t("vendor_route_auto_only")}
                      </div>
                    )}
                    {/* Send Note */}
                    <button onClick={() => onSendNote(client)}
                      className="flex items-center justify-center gap-1.5 bg-purple-50 text-purple-700 border border-purple-200 font-semibold py-3 rounded-xl text-sm active:bg-purple-100">
                      <MessageSquare className="w-4 h-4" /> {t("vendor_route_note")}
                    </button>
                  </div>

                  {/* Sell Item — only if stock enabled */}
                  {stockEnabled && (
                    <button onClick={() => onSellItem(client)}
                      className="w-full flex items-center justify-center gap-2 bg-indigo-50 text-indigo-700 border border-indigo-200 font-semibold py-3 rounded-xl text-sm active:bg-indigo-100">
                      <ShoppingCart className="w-4 h-4" /> {t("vendor_route_sell")}
                    </button>
                  )}

                  {/* View Receipt — available after check-in when stock is enabled */}
                  {client.last_status === "arrived" && stockEnabled && (
                    <button onClick={() => fetchAndShowReceipt(client)}
                      disabled={fetchingReceipt === client.id}
                      className="w-full flex items-center justify-center gap-2 bg-green-50 text-green-700 border border-green-200 font-semibold py-3 rounded-xl text-sm active:bg-green-100 disabled:opacity-60">
                      {fetchingReceipt === client.id
                        ? <Loader2 className="w-4 h-4 animate-spin" />
                        : <Receipt className="w-4 h-4" />}
                      {fetchingReceipt === client.id ? t("vendor_route_loading_receipt") : t("vendor_route_receipt")}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* Reprint Receipt Modal */}
      {receiptSale && <ReceiptModal sale={receiptSale} companyId={companyId} vendorProfile={vendorProfile} onClose={() => setReceiptSale(null)} />}

      {/* Required Note Modal — shown before check-in when require_notes_on_visit is true */}
      {checkinNoteModal && checkinNoteClient && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
          <div className="absolute inset-0 bg-black/50"
            onClick={() => { setCheckinNoteModal(false); setActionState(checkinNoteClient.id, "checking", false); }} />
          <div className="relative bg-white rounded-t-3xl sm:rounded-2xl w-full sm:max-w-sm shadow-2xl">
            <div className="flex items-center justify-between px-5 pt-5 pb-3">
              <h2 className="text-base font-bold text-gray-800">{t("vendor_route_checkin_note_title")}</h2>
              <button onClick={() => { setCheckinNoteModal(false); setActionState(checkinNoteClient.id, "checking", false); }}
                className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>
            <div className="px-5 pb-6 space-y-4">
              <p className="text-sm text-gray-500">{t("vendor_route_checkin_note_hint")} <strong>{checkinNoteClient.name || `${checkinNoteClient.first_name || ""} ${checkinNoteClient.last_name || ""}`.trim()}</strong>.</p>
              {checkinNoteError && (
                <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-3 text-sm flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 shrink-0" />{checkinNoteError}
                </div>
              )}
              <textarea value={checkinNote}
                onChange={e => setCheckinNote(e.target.value)}
                rows={3}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 resize-none"
                placeholder={t("vendor_route_checkin_note_ph")} />
              <div className="flex gap-3">
                <button
                  onClick={() => { setCheckinNoteModal(false); setActionState(checkinNoteClient.id, "checking", false); }}
                  className="flex-1 border border-gray-200 text-gray-600 font-medium py-3.5 rounded-2xl text-sm">
                  {t("vendor_note_cancel")}
                </button>
                <button
                  onClick={() => {
                    if (!checkinNote.trim()) { setCheckinNoteError(t("vendor_route_checkin_note_error")); return; }
                    setCheckinNoteModal(false);
                    handleCheckIn(checkinNoteClient, checkinNote);
                  }}
                  className="flex-1 bg-orange-600 hover:bg-orange-700 text-white font-bold py-3.5 rounded-2xl flex items-center justify-center gap-2 transition text-sm">
                  <CheckCircle className="w-4 h-4" /> {t("vendor_route_confirm_checkin")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PAGE 2 — My Map
// ─────────────────────────────────────────────────────────────────────────────
function MapPage({ companyId, currentUser, stockEnabled, vendorProfile, visitResetDays, onSendNote, onSellItem }) {
  const { t } = useLanguage();
  const [clients,    setClients]    = useState([]);
  const [mapType,    setMapType]    = useState("satellite");
  const [myPos,      setMyPos]      = useState(null);
  const [mySpeed,    setMySpeed]    = useState(0);
  const [selected,   setSelected]   = useState(null);
  const [mapCenter,  setMapCenter]  = useState(DEFAULT_CENTER);
  const mapRef = useRef(null);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  // Set initial map center based on company timezone before GPS kicks in
  useEffect(() => {
    async function loadMapCenter() {
      try {
        const snap = await getDoc(doc(db, "companies", companyId, "config", "settings"));
        if (snap.exists()) {
          const center = getMapCenter(snap.data().timezone || "Asia/Dubai");
          setMapCenter({ lat: center.lat, lng: center.lng });
        }
      } catch (err) { console.error("Could not load map center:", err.message); }
    }
    loadMapCenter();
  }, [companyId]);

  useEffect(() => {
    async function loadClients() {
      try {
        const snap = await getDocs(collection(db, "companies", companyId, "clients"));
        const all  = snap.docs.map(d => ({ id: d.id, ...d.data() }));

        // Filter by assigned vendor OR vendor's assigned section(s)
        const assignedSections = vendorProfile
          ? (Array.isArray(vendorProfile.assigned_sections)
              ? vendorProfile.assigned_sections
              : Array.isArray(vendorProfile.sections)
                ? vendorProfile.sections
                : vendorProfile.assigned_section
                  ? [vendorProfile.assigned_section]
                  : [])
          : [];

        const filtered = all.filter(c => {
          if (c.assigned_vendor === currentUser.uid) return true;
          if (assignedSections.length > 0 && c.section && assignedSections.includes(c.section)) return true;
          return false;
        });
        setClients(filtered);
      } catch (err) { console.error("map clients:", err.message); }
    }
    loadClients();

    // Watch GPS
    if (!navigator.geolocation) return;
    const watchId = navigator.geolocation.watchPosition(
      pos => {
        const p = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setMyPos(p);
        setMySpeed(pos.coords.speed || 0);
        setMapCenter(p);
      },
      () => {},
      { enableHighAccuracy: true, maximumAge: 5000 }
    );
    return () => navigator.geolocation.clearWatch(watchId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, currentUser.uid, vendorProfile]);

  function locateMe() {
    if (myPos && mapRef.current) mapRef.current.panTo(myPos);
  }

  function navigateTo(client) {
    const loc = client.location;
    if (loc?.lat && loc?.lng)
      window.open(`https://www.google.com/maps/dir/?api=1&destination=${loc.lat},${loc.lng}`, "_blank");
  }

  const mappable = clients.filter(c => c.location?.lat && c.location?.lng);

  return (
    <div className="relative overflow-hidden" style={{ height: "calc(100vh - 114px)" }}>
      {isLoaded ? (
        <GoogleMap
          mapContainerStyle={{ width: "100%", height: "100%" }}
          center={mapCenter}
          zoom={14}
          mapTypeId={mapType}
          onLoad={map => { mapRef.current = map; }}
          onClick={() => setSelected(null)}
          options={{ streetViewControl: false, mapTypeControl: false, fullscreenControl: false, zoomControl: true }}
        >
          {/* Client markers */}
          {mappable.map(client => (
            <Marker key={client.id}
              position={{ lat: client.location.lat, lng: client.location.lng }}
              onClick={() => setSelected(client)}
              icon={(() => {
                const visited = client.last_status === "arrived" || client.last_status === "done";
                const resetMs = (visitResetDays ?? 1) * 24 * 60 * 60 * 1000;
                const lastVisit = client.last_visited_at
                  ? (client.last_visited_at.toDate ? client.last_visited_at.toDate() : new Date(client.last_visited_at))
                  : null;
                const withinReset = lastVisit && (Date.now() - lastVisit.getTime()) < resetMs;
                return {
                  url: (visited && withinReset) ? "/icons/green-pin.png" : "/icons/red-pin.png",
                  scaledSize: new window.google.maps.Size(18, 18),
                  anchor: new window.google.maps.Point(9, 18),
                };
              })()}
            />
          ))}

          {/* Vendor marker */}
          {myPos && (
            <Marker position={myPos} title="My location"
              icon={{
                url: mySpeed > 0 ? "/icons/car-green.png" : "/icons/car.png",
                scaledSize: new window.google.maps.Size(40, 40),
                anchor: new window.google.maps.Point(20, 20),
              }}
            />
          )}

          {/* InfoWindow */}
          {selected && selected.location?.lat && (
            <InfoWindow
              position={{ lat: selected.location.lat, lng: selected.location.lng }}
              onCloseClick={() => setSelected(null)}>
              <div style={{ minWidth: 170, fontFamily: "sans-serif" }}>
                <p style={{ fontWeight: 700, fontSize: 14, marginBottom: 4, color: "#1f2937" }}>
                  {selected.name || `${selected.first_name || ""} ${selected.last_name || ""}`.trim()}
                </p>
                {selected.phone && (
                  <p style={{ fontSize: 12, color: "#4b5563", marginBottom: 2 }}>📞 {selected.phone}</p>
                )}
                {selected.address && (
                  <p style={{ fontSize: 12, color: "#6b7280", marginBottom: 6 }}>📍 {selected.address}</p>
                )}
                <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                  <button onClick={() => navigateTo(selected)}
                    style={{ flex: 1, minWidth: 60, background: "#2563eb", color: "white", border: "none", borderRadius: 8, padding: "6px 0", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
                    {t("vendor_route_navigate")}
                  </button>
                  <button onClick={() => { setSelected(null); onSendNote(selected); }}
                    style={{ flex: 1, minWidth: 60, background: "#7c3aed", color: "white", border: "none", borderRadius: 8, padding: "6px 0", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
                    {t("vendor_route_note")}
                  </button>
                  {stockEnabled && (
                    <button onClick={() => { setSelected(null); onSellItem(selected); }}
                      style={{ width: "100%", background: "#4f46e5", color: "white", border: "none", borderRadius: 8, padding: "6px 0", fontSize: 11, fontWeight: 600, cursor: "pointer", marginTop: 4 }}>
                      {t("vendor_route_sell")}
                    </button>
                  )}
                </div>
              </div>
            </InfoWindow>
          )}
        </GoogleMap>
      ) : (
        <div className="w-full h-full bg-gray-200 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      )}

      {/* Map type toggle */}
      <div className="absolute top-3 left-3 z-10">
        <button onClick={() => setMapType(mt => mt === "satellite" ? "roadmap" : "satellite")}
          className="flex items-center gap-1.5 bg-white/90 backdrop-blur-sm text-gray-700 text-xs font-medium px-3 py-2.5 rounded-xl shadow-lg border border-white/50">
          {mapType === "satellite"
            ? <><MapIcon className="w-4 h-4 text-blue-600" /> {t("vendor_map_view")}</>
            : <><Globe  className="w-4 h-4 text-orange-500" /> {t("vendor_map_satellite")}</>
          }
        </button>
      </div>

      {/* Locate me */}
      <div className="absolute z-10" style={{ bottom: 120, right: 10 }}>
        <button onClick={locateMe}
          className="w-12 h-12 rounded-full bg-white shadow-lg border border-gray-200 flex items-center justify-center active:bg-gray-50">
          <Crosshair className="w-5 h-5 text-gray-700" />
        </button>
      </div>

      {/* Client count */}
      <div className="absolute bottom-4 left-3 z-10 pointer-events-none">
        <div className="bg-white/90 text-gray-600 text-xs font-medium px-3 py-1.5 rounded-xl shadow">
          {mappable.length} client{mappable.length !== 1 ? "s" : ""} on map
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PAGE 3 — My Statistics
// ─────────────────────────────────────────────────────────────────────────────
function StatsPage({ companyId, currentUser, vendorDocId, companyTimezone = "Asia/Dubai" }) {
  const { t } = useLanguage();
  const [events,   setEvents]   = useState([]);
  const [profile,  setProfile]  = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState("");

  useEffect(() => {
    if (!companyId || !currentUser?.uid) return;
    async function load() {
      try {
        // Run separate queries per vendor ID to avoid composite index requirement.
        // Backend GPS events use the Firestore doc ID as vendor_id; frontend events
        // use currentUser.uid (Auth UID). Both are needed for a complete count.
        const docId = vendorDocId || currentUser.uid;
        const queryUid = getDocs(query(
          collection(db, "companies", companyId, "events"),
          where("vendor_id", "==", currentUser.uid),
          where("type", "==", "checkin"),
          limit(500)
        ));
        const queryDocId = (docId !== currentUser.uid)
          ? getDocs(query(
              collection(db, "companies", companyId, "events"),
              where("vendor_id", "==", docId),
              where("type", "==", "checkin"),
              limit(500)
            ))
          : Promise.resolve(null);

        const [snapUid, snapDocId, profileSnap] = await Promise.all([
          queryUid,
          queryDocId,
          getDoc(doc(db, "companies", companyId, "users", docId)),
        ]);

        const seen = new Set();
        const eventList = [];
        for (const snap of [snapUid, snapDocId]) {
          if (!snap) continue;
          snap.docs.forEach(d => {
            if (!seen.has(d.id)) { seen.add(d.id); eventList.push({ id: d.id, ...d.data() }); }
          });
        }
        // Sort newest-first in JavaScript — avoids orderBy composite index requirement
        eventList.sort((a, b) => {
          const tA = a.timestamp?.toDate ? a.timestamp.toDate() : new Date(a.timestamp || 0);
          const tB = b.timestamp?.toDate ? b.timestamp.toDate() : new Date(b.timestamp || 0);
          return tB - tA;
        });
        setEvents(eventList);
        if (profileSnap.exists()) setProfile(profileSnap.data());
      } catch (err) {
        setError("Error: " + err.message);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [companyId, currentUser?.uid, vendorDocId]);

  function toDate(ts) {
    return ts?.toDate ? ts.toDate() : new Date(ts || 0);
  }

  const now        = new Date();
  const todayStart = startOfDayInTimezone(companyTimezone);
  const todayKey   = todayStringInTimezone(companyTimezone);
  const weekStart  = new Date(todayStart.getTime() - 7 * 86400000);

  const todayVisits = events.filter(e => toDate(e.timestamp).toLocaleDateString("en-CA", { timeZone: companyTimezone }) === todayKey).length;
  const weekVisits  = events.filter(e => toDate(e.timestamp) >= weekStart).length;
  const totalVisits = events.length;

  // Days since account created
  const createdAt  = profile?.created_at ? toDate(profile.created_at) : null;
  const daysSince  = createdAt ? Math.max(1, Math.floor((now - createdAt) / 86400000)) : 1;
  const avgPerDay  = (totalVisits / daysSince).toFixed(1);

  const manualCount = events.filter(e => !e.auto).length;
  const autoCount   = events.filter(e => e.auto === true).length;

  // Last 7 days chart — bucketed in company timezone
  const last7 = Array.from({ length: 7 }, (_, i) => {
    const d      = new Date(todayStart.getTime() - (6 - i) * 86400000);
    const dayKey = d.toLocaleDateString("en-CA", { timeZone: companyTimezone });
    const label  = d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", timeZone: companyTimezone });
    return {
      day:    label,
      visits: events.filter(e => toDate(e.timestamp).toLocaleDateString("en-CA", { timeZone: companyTimezone }) === dayKey).length,
    };
  });

  // Last 4 weeks chart
  const last4w = Array.from({ length: 4 }, (_, i) => {
    const wEnd   = new Date(now); wEnd.setDate(now.getDate() - i * 7);
    const wStart = new Date(wEnd); wStart.setDate(wEnd.getDate() - 6);
    return {
      week:   `W${4 - i}`,
      visits: events.filter(e => {
        const d = toDate(e.timestamp);
        return d >= wStart && d <= wEnd;
      }).length,
    };
  }).reverse();

  // Recent 10
  const recent10 = events.slice(0, 10);

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
    </div>
  );

  return (
    <div className="p-4 space-y-4">
      <div className="bg-white rounded-xl shadow-sm p-4">
        <h2 className="text-base font-bold text-gray-800">{t("vendor_stats_title")}</h2>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-3 text-sm">
          {error}
        </div>
      )}

      {/* Stat cards */}
      <div className="grid grid-cols-2 gap-3">
        {[
          { label: t("vendor_stats_today"), value: todayVisits, color: "text-blue-600",  bg: "bg-blue-50"  },
          { label: t("vendor_stats_week"),  value: weekVisits,  color: "text-green-600", bg: "bg-green-50" },
          { label: t("vendor_stats_total"), value: totalVisits, color: "text-purple-600",bg: "bg-purple-50"},
          { label: t("vendor_stats_avg"),   value: avgPerDay,   color: "text-orange-600",bg: "bg-orange-50"},
        ].map((s, i) => (
          <div key={i} className={`${s.bg} rounded-xl p-4 shadow-sm`}>
            <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
            <p className="text-xs text-gray-500 mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Visit breakdown — manual vs GPS auto */}
      <div className="bg-white rounded-xl shadow-sm p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-2">{t("vendor_stats_breakdown")}</h3>
        <div className="flex gap-6">
          <span className="text-xs text-gray-500">{t("vendor_stats_manual")}: <span className="font-semibold text-gray-700">{manualCount}</span></span>
          <span className="text-xs text-gray-500">{t("vendor_stats_auto")}: <span className="font-semibold text-gray-700">{autoCount}</span></span>
        </div>
      </div>

      {/* Last 7 days */}
      <div className="bg-white rounded-xl shadow-sm p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">{t("vendor_stats_last7")}</h3>
        <ResponsiveContainer width="100%" height={180}>
          <BarChart data={last7}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="day" tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
            <Tooltip />
            <Bar dataKey="visits" fill="#2563eb" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Last 4 weeks */}
      <div className="bg-white rounded-xl shadow-sm p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">{t("vendor_stats_last4w")}</h3>
        <ResponsiveContainer width="100%" height={180}>
          <BarChart data={last4w}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="week" tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
            <Tooltip />
            <Bar dataKey="visits" fill="#16a34a" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Recent activity */}
      <div className="bg-white rounded-xl shadow-sm p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">{t("vendor_stats_recent")}</h3>
        {recent10.length === 0 ? (
          <p className="text-gray-400 text-sm text-center py-4">{t("vendor_stats_no_checkins")}</p>
        ) : (
          <div className="space-y-2">
            {recent10.map((e, i) => (
              <div key={e.id || i} className="flex items-center gap-3 py-2 border-b border-gray-50 last:border-0">
                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                  <CheckCircle className="w-4 h-4 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{e.client_name || "—"}</p>
                  {e.distance_from_client != null && (
                    <p className="text-xs text-gray-400">{e.distance_from_client}m away</p>
                  )}
                </div>
                <p className="text-xs text-gray-400 shrink-0">
                  {e.timestamp
                    ? toDate(e.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
                    : "—"}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PAGE 4 — My Stock
// ─────────────────────────────────────────────────────────────────────────────
function StockPage({ companyId, currentUser, vendorProfile, companyTimezone = "Asia/Dubai" }) {
  const { t } = useLanguage();
  const [items,          setItems]          = useState([]);
  const [sales,          setSales]          = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [error,          setError]          = useState("");
  const [restockModalOpen,     setRestockModalOpen]     = useState(false);
  const [restockCatalog,       setRestockCatalog]       = useState([]);
  const [restockCatalogLoad,   setRestockCatalogLoad]   = useState(false);
  const [restockQtys,          setRestockQtys]          = useState({}); // { [itemId]: string }
  const [restockSearch,        setRestockSearch]        = useState("");
  const [restockNote,          setRestockNote]          = useState("");
  const [restockSaving,        setRestockSaving]        = useState(false);
  const [restockSuccess,       setRestockSuccess]       = useState("");
  const [restockError,         setRestockError]         = useState("");
  const [printSale,      setPrintSale]      = useState(null); // sale to reprint

  useEffect(() => {
    async function load() {
      try {
        const salesList = [];

        if (!navigator.onLine) {
          // Offline — serve from cache
          const [cachedStock, offlineSales] = await Promise.all([
            getStockOffline(currentUser.uid),
            getPendingSales(),
          ]);
          setItems(cachedStock);
          salesList.push(...offlineSales.map(s => ({
            ...s,
            id:         s.localId,
            source:     "offline",
            created_at: { toDate: () => new Date(s.savedAt) },
          })));
          setSales(salesList);
          setLoading(false);
          return;
        }

        const [itemsSnap, salesSnap, offlineSales] = await Promise.all([
          getDocs(collection(db, "companies", companyId, "vendor_stock", currentUser.uid, "items")),
          getDocs(query(
            collection(db, "companies", companyId, "sales"),
            where("vendor_id", "==", currentUser.uid),
            limit(50)
          )),
          getPendingSales(),
        ]);
        const stockList = itemsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        setItems(stockList);
        // Cache for offline use
        saveStockOffline(stockList, currentUser.uid).catch(() => {});

        // Online sales
        salesList.push(...salesSnap.docs.map(d => ({
          id: d.id, ...d.data(), source: "online",
        })));
        // Pending offline sales not yet synced
        salesList.push(...offlineSales.map(s => ({
          ...s,
          id:         s.localId,
          source:     "offline",
          created_at: { toDate: () => new Date(s.savedAt) },
        })));

        // Sort newest first
        salesList.sort((a, b) => {
          const ta = a.created_at?.toDate ? a.created_at.toDate() : new Date(0);
          const tb = b.created_at?.toDate ? b.created_at.toDate() : new Date(0);
          return tb - ta;
        });
        setSales(salesList.slice(0, 50));
      } catch (err) {
        setError("Error: " + err.message + (err.code ? ` (${err.code})` : ""));
      }
      setLoading(false);
    }
    load();
  }, [companyId, currentUser.uid]);

  function formatTime(ts) {
    if (!ts) return "—";
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleString([], { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", timeZone: companyTimezone });
  }

  function isToday(ts) {
    if (!ts) return false;
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString("en-CA", { timeZone: companyTimezone }) === todayStringInTimezone(companyTimezone);
  }

  function isThisWeek(ts) {
    if (!ts) return false;
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    const weekAgo = new Date(startOfDayInTimezone(companyTimezone).getTime() - 7 * 86400000);
    return d >= weekAgo;
  }

  const totalProducts = items.length;
  const totalStock    = items.reduce((s, i) => s + (i.quantity || 0), 0);
  const salesToday    = sales.filter(s => isToday(s.created_at)).length;
  const salesWeek     = sales.filter(s => isThisWeek(s.created_at)).length;
  const revToday      = sales.filter(s => isToday(s.created_at)).reduce((sum, s) => sum + (s.total_amount || 0), 0);

  async function openRestockModal() {
    setRestockModalOpen(true);
    setRestockCatalogLoad(true);
    setRestockQtys({});
    setRestockSearch("");
    setRestockNote("");
    setRestockSuccess("");
    setRestockError("");
    try {
      const snap = await getDocs(collection(db, "companies", companyId, "stock_catalog"));
      const cat  = snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(i => i.active !== false);
      cat.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      setRestockCatalog(cat);
      const qtys = {};
      cat.forEach(item => { qtys[item.id] = ""; });
      setRestockQtys(qtys);
    } catch (err) {
      setRestockError("Failed to load products: " + err.message);
    }
    setRestockCatalogLoad(false);
  }

  function closeRestockModal() {
    setRestockModalOpen(false);
    setRestockCatalog([]);
    setRestockQtys({});
    setRestockSearch("");
    setRestockNote("");
    setRestockSuccess("");
    setRestockError("");
  }

  async function sendRestockRequest() {
    const selectedItems = restockCatalog.filter(item => parseInt(restockQtys[item.id], 10) > 0);
    if (selectedItems.length === 0) { setRestockError("Select at least one product and enter a quantity."); return; }
    if (!restockNote.trim()) { setRestockError("Note to admin is required."); return; }
    setRestockSaving(true);
    setRestockError("");
    try {
      const vendorName = vendorProfile
        ? `${vendorProfile.first_name || ""} ${vendorProfile.last_name || ""}`.trim() || currentUser.email
        : (currentUser.displayName || currentUser.email);
      const requestItems = selectedItems.map(item => ({
        item_id:       item.id,
        item_name:     item.name,
        item_unit:     item.unit || "",
        requested_qty: parseInt(restockQtys[item.id], 10),
      }));
      await addDoc(collection(db, "companies", companyId, "restock_requests"), {
        vendor_id:    currentUser.uid,
        vendor_name:  vendorName,
        vendor_phone: vendorProfile?.phone || "",
        items:        requestItems,
        note:         restockNote.trim(),
        status:       "pending",
        created_at:   serverTimestamp(),
      });
      setRestockSuccess(`Request sent for ${requestItems.length} product${requestItems.length !== 1 ? "s" : ""}. Your admin will review it.`);
    } catch (err) {
      setRestockError("Failed to send: " + err.message);
    }
    setRestockSaving(false);
  }

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
    </div>
  );

  return (
    <div className="p-4 space-y-4">

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-3 text-sm">{error}</div>
      )}

      {/* Stat cards */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-white rounded-xl shadow-sm p-4">
          <div className="flex items-center gap-2 mb-1">
            <Package className="w-4 h-4 text-blue-500" />
            <span className="text-xs text-gray-500 font-medium">{t("vendor_stock_products")}</span>
          </div>
          <p className="text-2xl font-bold text-gray-800">{totalProducts}</p>
          <p className="text-xs text-gray-400">{totalStock} {t("vendor_stock_units")}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-4">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp className="w-4 h-4 text-green-500" />
            <span className="text-xs text-gray-500 font-medium">{t("vendor_stock_sales_today")}</span>
          </div>
          <p className="text-2xl font-bold text-gray-800">{salesToday}</p>
          <p className="text-xs text-gray-400">{salesWeek} {t("vendor_stock_this_week")}</p>
        </div>
      </div>

      {/* Current stock */}
      <div className="bg-white rounded-xl shadow-sm p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-700">{t("vendor_stock_current")}</h3>
          <button onClick={openRestockModal}
            className="flex items-center gap-1.5 text-xs font-semibold bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-xl active:bg-blue-800 transition">
            <RefreshCw className="w-3.5 h-3.5" /> {t("vendor_stock_request_restock")}
          </button>
        </div>
        {items.length === 0 ? (
          <p className="text-gray-400 text-sm text-center py-4">{t("vendor_stock_no_items")}</p>
        ) : (
          <div className="space-y-1">
            {items.map(item => {
              const isLow = item.quantity <= (item.low_stock_threshold || 0);
              return (
                <div key={item.id} className="flex items-center justify-between py-3 border-b border-gray-50 last:border-0 gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-800">{item.name}</p>
                    <p className="text-xs text-gray-400">{item.unit}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className={`text-lg font-bold ${isLow ? "text-red-600" : "text-gray-800"}`}>
                      {item.quantity}
                    </p>
                    {isLow && (
                      <span className="text-[10px] bg-red-100 text-red-600 font-bold px-1.5 py-0.5 rounded-full block">
                        {t("vendor_stock_low")}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Recent sales */}
      <div className="bg-white rounded-xl shadow-sm p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-700">{t("vendor_stock_recent_sales")}</h3>
          {revToday > 0 && (
            <span className="text-xs font-semibold text-green-700 bg-green-50 px-2.5 py-1 rounded-full">
              {t("vendor_stock_today_revenue")}: {revToday.toFixed(2)}
            </span>
          )}
        </div>
        {sales.length === 0 ? (
          <p className="text-gray-400 text-sm text-center py-4">{t("vendor_stock_no_sales")}</p>
        ) : (
          <div className="space-y-3">
            {sales.map(sale => (
              <div key={sale.id} className={`border rounded-xl p-3 ${sale.source === "offline" ? "border-orange-200 bg-orange-50/40" : "border-gray-100"}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-2 min-w-0">
                    <p className="text-sm font-semibold text-gray-800 truncate">{sale.client_name || "—"}</p>
                    {sale.source === "offline" && (
                      <span className="shrink-0 inline-flex items-center gap-0.5 text-[10px] bg-orange-100 text-orange-600 px-1.5 py-0.5 rounded-full">
                        <WifiOff className="w-2.5 h-2.5" /> {t("vendor_stock_pending_sync")}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <p className="text-xs text-gray-400">{formatTime(sale.created_at)}</p>
                    {sale.source !== "offline" && (
                      <button onClick={() => setPrintSale(sale)}
                        className="flex items-center gap-1 text-xs text-gray-500 border border-gray-200 bg-gray-50 px-2 py-1 rounded-lg active:bg-gray-100">
                        <Printer className="w-3 h-3" /> {t("vendor_stock_print")}
                      </button>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap gap-1 mb-1.5">
                  {(sale.items || []).map((it, i) => (
                    <span key={i} className="text-xs bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded-full">
                      {it.item_name} × {it.quantity}
                    </span>
                  ))}
                </div>
                {sale.total_amount > 0 && (
                  <p className="text-xs font-bold text-gray-700">Total: {sale.total_amount.toFixed(2)}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Restock Request Modal — full catalog, multi-product */}
      {restockModalOpen && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={closeRestockModal} />
          <div className="relative bg-white rounded-t-3xl sm:rounded-2xl w-full sm:max-w-sm max-h-[90vh] flex flex-col shadow-2xl">

            {/* Header */}
            <div className="flex items-center justify-between px-5 pt-5 pb-3 shrink-0">
              <div className="flex items-center gap-2">
                <RefreshCw className="w-5 h-5 text-blue-600" />
                <h2 className="text-base font-bold text-gray-800">{t("vendor_stock_request_restock")}</h2>
              </div>
              <button onClick={closeRestockModal}
                className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>

            {restockSuccess ? (
              <div className="px-5 pb-6 space-y-4 shrink-0">
                <div className="bg-green-50 border border-green-200 text-green-700 rounded-xl p-4 text-sm text-center">
                  {restockSuccess}
                </div>
                <button onClick={closeRestockModal}
                  className="w-full border border-gray-200 text-gray-600 font-medium py-3.5 rounded-2xl text-sm">
                  Close
                </button>
              </div>
            ) : (
              <>
                {/* Search */}
                <div className="px-5 pb-2 shrink-0">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      value={restockSearch}
                      onChange={e => setRestockSearch(e.target.value)}
                      placeholder="Search products..."
                      className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
                  </div>
                </div>

                {/* Product list */}
                <div className="flex-1 overflow-y-auto px-5 pb-2 space-y-2">
                  {restockCatalogLoad ? (
                    <div className="py-8 flex justify-center">
                      <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                    </div>
                  ) : restockCatalog.length === 0 ? (
                    <p className="text-gray-400 text-sm text-center py-6">No products in catalog</p>
                  ) : (
                    (() => {
                      // build vendor stock map for quick lookup
                      const vendorStockMap = {};
                      items.forEach(i => { vendorStockMap[i.id] = i.quantity || 0; });

                      const catalogFiltered = restockSearch.trim()
                        ? restockCatalog.filter(i => i.name?.toLowerCase().includes(restockSearch.toLowerCase()))
                        : restockCatalog;

                      return catalogFiltered.map(item => {
                        const qty         = restockQtys[item.id] || "";
                        const currentQty  = vendorStockMap[item.id] ?? null; // null = not in vendor stock
                        return (
                          <div key={item.id}
                            className={`flex items-center gap-3 rounded-xl p-3 border transition ${parseInt(qty, 10) > 0 ? "bg-blue-50 border-blue-200" : "bg-gray-50 border-transparent"}`}>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-gray-800">{item.name}</p>
                              <p className="text-xs text-gray-400">
                                {item.unit}
                                {currentQty !== null
                                  ? <span className="ml-2 text-gray-500">· my stock: <span className={currentQty <= (item.low_stock_threshold || 0) ? "text-red-500 font-semibold" : ""}>{currentQty}</span></span>
                                  : <span className="ml-2 text-gray-400 italic">· not in my stock</span>}
                              </p>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <button
                                onClick={() => setRestockQtys(p => ({ ...p, [item.id]: String(Math.max(0, (parseInt(p[item.id], 10) || 0) - 1) || "") }))}
                                className="w-9 h-9 rounded-full bg-white border border-gray-200 flex items-center justify-center text-gray-600 active:bg-gray-100 text-lg font-bold">
                                −
                              </button>
                              <input
                                type="number"
                                min="0"
                                value={qty}
                                onChange={e => setRestockQtys(p => ({ ...p, [item.id]: e.target.value }))}
                                className="w-12 text-center border border-gray-200 rounded-lg py-1.5 text-sm font-bold focus:outline-none focus:ring-2 focus:ring-blue-400"
                              />
                              <button
                                onClick={() => setRestockQtys(p => ({ ...p, [item.id]: String((parseInt(p[item.id], 10) || 0) + 1) }))}
                                className="w-9 h-9 rounded-full bg-white border border-gray-200 flex items-center justify-center text-gray-600 active:bg-gray-100 text-lg font-bold">
                                +
                              </button>
                            </div>
                          </div>
                        );
                      });
                    })()
                  )}
                </div>

                {/* Selected count badge */}
                {(() => {
                  const count = restockCatalog.filter(i => parseInt(restockQtys[i.id], 10) > 0).length;
                  return count > 0 ? (
                    <div className="mx-5 mb-2 bg-blue-50 border border-blue-200 rounded-xl px-4 py-2 shrink-0">
                      <p className="text-sm text-blue-700 font-medium text-center">
                        {count} product{count !== 1 ? "s" : ""} selected
                      </p>
                    </div>
                  ) : null;
                })()}

                {/* Note + error + submit */}
                <div className="px-5 pb-6 shrink-0 space-y-3">
                  {restockError && (
                    <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-3 text-sm">{restockError}</div>
                  )}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Note to Admin <span className="text-red-400">*</span>
                    </label>
                    <textarea
                      value={restockNote}
                      onChange={e => setRestockNote(e.target.value)}
                      rows={2}
                      placeholder="e.g. Running low on several items, need urgently"
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none" />
                  </div>
                  <div className="flex gap-3">
                    <button onClick={closeRestockModal}
                      className="flex-1 border border-gray-200 text-gray-600 font-medium py-3.5 rounded-2xl text-sm">
                      {t("vendor_note_cancel")}
                    </button>
                    <button onClick={sendRestockRequest} disabled={restockSaving}
                      className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-bold py-3.5 rounded-2xl flex items-center justify-center gap-2 transition text-sm">
                      {restockSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      {restockSaving ? t("vendor_note_sending") : t("vendor_send_note_title")}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Reprint Receipt Modal */}
      {printSale && <ReceiptModal sale={printSale} companyId={companyId} vendorProfile={vendorProfile} onClose={() => setPrintSale(null)} />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PAGE 5 — My Profile
// ─────────────────────────────────────────────────────────────────────────────
function ProfilePage({ companyId, currentUser, vendorProfile, onSendNote, isOnline, pendingCount, lastSyncTime, onSync, syncing, isDarkMode, toggleDarkMode }) {
  const { t } = useLanguage();
  const [pendingSalesCount, setPendingSalesCount] = React.useState(0);
  React.useEffect(() => {
    getPendingSales().then(s => setPendingSalesCount(s.length)).catch(() => {});
  }, [pendingCount]); // refresh whenever pendingCount changes
  const [currentPw,   setCurrentPw]   = useState("");
  const [newPw,       setNewPw]       = useState("");
  const [confirmPw,   setConfirmPw]   = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew,     setShowNew]     = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pwSaving,    setPwSaving]    = useState(false);
  const [pwError,     setPwError]     = useState("");
  const [pwSuccess,   setPwSuccess]   = useState("");

  async function handleChangePassword() {
    setPwError(""); setPwSuccess("");
    if (!currentPw || !newPw || !confirmPw) return setPwError(t("vendor_profile_pw_short"));
    if (newPw !== confirmPw) return setPwError(t("vendor_profile_pw_mismatch"));
    if (newPw.length < 6) return setPwError(t("vendor_profile_pw_short"));
    setPwSaving(true);
    try {
      const auth       = getAuth();
      const credential = EmailAuthProvider.credential(currentUser.email, currentPw);
      await reauthenticateWithCredential(auth.currentUser, credential);
      await updatePassword(auth.currentUser, newPw);
      setPwSuccess(t("vendor_profile_pw_saved"));
      setCurrentPw(""); setNewPw(""); setConfirmPw("");
    } catch (err) {
      setPwError("Error: " + (err.message || err.code));
    }
    setPwSaving(false);
  }

  const p = vendorProfile;

  return (
    <div className="p-4 space-y-4">
      {/* Profile card */}
      <div className="bg-white rounded-xl shadow-sm p-5">
        <div className="flex items-center gap-4 mb-4">
          <div className="w-14 h-14 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xl font-bold shrink-0">
            {(p?.first_name?.[0] || currentUser.email?.[0] || "V").toUpperCase()}
          </div>
          <div>
            <p className="font-bold text-gray-800 text-lg">
              {p ? `${p.first_name || ""} ${p.last_name || ""}`.trim() : currentUser.email}
            </p>
            <p className="text-sm text-gray-500">{currentUser.email}</p>
          </div>
        </div>
        <div className="space-y-2 text-sm">
          {p?.phone && (
            <div className="flex items-center gap-2 text-gray-600">
              <Phone className="w-4 h-4 text-gray-400 shrink-0" />
              {p.phone}
            </div>
          )}
          {p?.plate && (
            <div className="flex items-center gap-2 text-gray-600">
              <Truck className="w-4 h-4 text-gray-400 shrink-0" />
              Plate: {p.plate}
            </div>
          )}
          {p?.imei && (
            <div className="flex items-center gap-2 text-gray-500 text-xs">
              <span className="text-gray-400">IMEI:</span> {p.imei}
            </div>
          )}
        </div>
      </div>

      {/* General note */}
      <button onClick={onSendNote}
        className="w-full flex items-center justify-center gap-2 bg-purple-50 text-purple-700 border border-purple-200 font-semibold py-3.5 rounded-2xl text-sm active:bg-purple-100">
        <MessageSquare className="w-4 h-4" /> {t("vendor_profile_send_note")}
      </button>

      {/* Change password */}
      <div className="bg-white rounded-xl shadow-sm p-5">
        <h3 className="text-sm font-bold text-gray-700 mb-4 flex items-center gap-2">
          <Lock className="w-4 h-4" /> {t("vendor_profile_change_pw")}
        </h3>
        {pwError && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-3 text-sm flex items-center gap-2 mb-3">
            <AlertTriangle className="w-4 h-4 shrink-0" />{pwError}
          </div>
        )}
        {pwSuccess && (
          <div className="bg-green-50 border border-green-200 text-green-700 rounded-xl p-3 text-sm mb-3">
            {pwSuccess}
          </div>
        )}
        <div className="space-y-3">
          {[
            { label: t("vendor_profile_current_pw"), val: currentPw, set: setCurrentPw, show: showCurrent, toggle: setShowCurrent },
            { label: t("vendor_profile_new_pw"),     val: newPw,     set: setNewPw,     show: showNew,    toggle: setShowNew     },
            { label: t("vendor_profile_confirm_pw"), val: confirmPw, set: setConfirmPw, show: showConfirm, toggle: setShowConfirm },
          ].map((f, i) => (
            <div key={i}>
              <label className="block text-sm font-medium text-gray-700 mb-1">{f.label}</label>
              <div className="relative">
                <input
                  type={f.show ? "text" : "password"}
                  value={f.val}
                  onChange={e => f.set(e.target.value)}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-blue-400 pr-12" />
                <button type="button" onClick={() => f.toggle(!f.show)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                  {f.show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          ))}
        </div>
        <button onClick={handleChangePassword} disabled={pwSaving}
          className="w-full mt-4 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-bold py-3.5 rounded-2xl flex items-center justify-center gap-2 transition">
          {pwSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
          {pwSaving ? t("vendor_profile_updating") : t("vendor_profile_update_pw")}
        </button>
      </div>

      {/* Appearance */}
      <div className={`rounded-xl shadow-sm p-5 ${isDarkMode ? "bg-gray-800" : "bg-white"}`}>
        <h3 className={`text-sm font-bold mb-4 flex items-center gap-2 ${isDarkMode ? "text-gray-200" : "text-gray-700"}`}>
          <Moon className="w-4 h-4" /> {t("vendor_profile_appearance")}
        </h3>
        <div className="flex items-center justify-between">
          <div>
            <p className={`text-sm font-medium ${isDarkMode ? "text-gray-200" : "text-gray-800"}`}>{t("vendor_profile_dark_mode")}</p>
            <p className={`text-xs mt-0.5 ${isDarkMode ? "text-gray-400" : "text-gray-400"}`}>{t("vendor_profile_dark_hint")}</p>
          </div>
          <button
            onClick={toggleDarkMode}
            className={`relative inline-flex items-center rounded-full w-11 h-6 transition-colors focus:outline-none ${isDarkMode ? "bg-blue-600" : "bg-gray-300"}`}>
            <span className={`inline-block w-5 h-5 bg-white rounded-full shadow transform transition-transform ${isDarkMode ? "translate-x-5" : "translate-x-0.5"}`} />
          </button>
        </div>
      </div>

      {/* Offline data */}
      <div className={`rounded-xl shadow-sm p-5 ${isDarkMode ? "bg-gray-800" : "bg-white"}`}>
        <h3 className={`text-sm font-bold mb-4 flex items-center gap-2 ${isDarkMode ? "text-gray-200" : "text-gray-700"}`}>
          <WifiOff className="w-4 h-4" /> {t("vendor_profile_sync_status")}
        </h3>
        <div className="space-y-2 text-sm text-gray-600">
          <div className="flex justify-between">
            <span>{t("vendor_profile_sync_status")}</span>
            <span className={`font-semibold ${isOnline ? "text-green-600" : "text-orange-600"}`}>
              {isOnline ? t("vendor_profile_online") : t("vendor_profile_offline_msg")}
            </span>
          </div>
          <div className="flex justify-between">
            <span>{t("vendor_profile_pending")}</span>
            <span className={`font-semibold ${pendingCount > 0 ? "text-orange-600" : "text-gray-400"}`}>
              {pendingCount}
            </span>
          </div>
          {pendingSalesCount > 0 && (
            <div className="flex justify-between pl-4">
              <span className="text-gray-400 text-xs">{t("vendor_profile_pending")}</span>
              <span className="text-orange-500 font-semibold text-xs">{pendingSalesCount}</span>
            </div>
          )}
          <div className="flex justify-between">
            <span>{t("vendor_profile_last_sync")}</span>
            <span className="text-gray-400 text-xs">
              {lastSyncTime
                ? new Date(lastSyncTime).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
                : t("vendor_profile_never")}
            </span>
          </div>
        </div>
        <div className="mt-4 flex gap-2">
          {isOnline && pendingCount > 0 && (
            <button onClick={onSync} disabled={syncing}
              className="flex-1 flex items-center justify-center gap-2 bg-blue-50 text-blue-700 border border-blue-200 font-semibold py-3 rounded-xl text-sm disabled:opacity-60">
              {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              {syncing ? t("vendor_syncing") : t("vendor_sync_now")}
            </button>
          )}
          <button
            onClick={async () => {
              if (!window.confirm("Clear all offline cached data? Unsynced check-ins will be lost.")) return;
              await clearOfflineCache();
            }}
            className="flex-1 flex items-center justify-center gap-2 bg-red-50 text-red-600 border border-red-200 font-semibold py-3 rounded-xl text-sm">
            Clear Cache
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sell Item Modal — full POS
// ─────────────────────────────────────────────────────────────────────────────
function SellItemModal({ companyId, currentUser, vendorProfile, client, onClose }) {
  const [catalog,     setCatalog]     = useState([]);
  const [stockItems,  setStockItems]  = useState([]);
  const [quantities,  setQuantities]  = useState({});
  const [search,      setSearch]      = useState("");
  const [loading,     setLoading]     = useState(true);
  const [saving,      setSaving]      = useState(false);
  const [error,       setError]       = useState("");
  const [receipt,     setReceipt]     = useState(null);
  const [companyInfo, setCompanyInfo] = useState({ name: "", contact: "" });

  useEffect(() => {
    async function load() {
      try {
        if (!navigator.onLine) {
          // Offline — serve from IndexedDB cache
          const [cachedCat, cachedStock] = await Promise.all([
            getCatalogOffline(),
            getStockOffline(currentUser.uid),
          ]);
          const cat = cachedCat.filter(i => i.active !== false);
          setCatalog(cat);
          setStockItems(cachedStock);
          const q = {};
          cat.forEach(item => { q[item.id] = 0; });
          setQuantities(q);
          setLoading(false);
          return;
        }

        const [catalogSnap, stockSnap, rootSnap, profileSnap] = await Promise.all([
          getDocs(collection(db, "companies", companyId, "stock_catalog")),
          getDocs(collection(db, "companies", companyId, "vendor_stock", currentUser.uid, "items")),
          getDoc(doc(db, "companies", companyId)),
          getDoc(doc(db, "companies", companyId, "profile", "info")),
        ]);
        const cat   = catalogSnap.docs.map(d => ({ id: d.id, ...d.data() })).filter(i => i.active !== false);
        const stock = stockSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        setCatalog(cat);
        setStockItems(stock);
        const q = {};
        cat.forEach(item => { q[item.id] = 0; });
        setQuantities(q);
        setCompanyInfo({
          name:    rootSnap.exists()    ? (rootSnap.data().name    || "") : "",
          contact: profileSnap.exists() ? (profileSnap.data().phone || "") : "",
        });
        // Cache for offline use
        const allCat = catalogSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        saveCatalogOffline(allCat).catch(() => {});
        saveStockOffline(stock, currentUser.uid).catch(() => {});
      } catch (err) {
        // Firestore failed — try cache as fallback
        if (!navigator.onLine) {
          try {
            const [cachedCat, cachedStock] = await Promise.all([
              getCatalogOffline(),
              getStockOffline(currentUser.uid),
            ]);
            const cat = cachedCat.filter(i => i.active !== false);
            setCatalog(cat);
            setStockItems(cachedStock);
            const q = {};
            cat.forEach(item => { q[item.id] = 0; });
            setQuantities(q);
          } catch {
            setError("Error loading stock: " + err.message + (err.code ? ` (${err.code})` : ""));
          }
        } else {
          setError("Error loading stock: " + err.message + (err.code ? ` (${err.code})` : ""));
        }
      }
      setLoading(false);
    }
    load();
  }, [companyId, currentUser.uid]);

  function getStockQty(catalogId) {
    return stockItems.find(s => s.id === catalogId)?.quantity || 0;
  }

  const clientName = client?.name ||
    `${client?.first_name || ""} ${client?.last_name || ""}`.trim();

  const selectedItems = catalog.filter(item => quantities[item.id] > 0);
  const totalAmount   = selectedItems.reduce((sum, item) => {
    const price = item.selling_price || 0;
    return sum + price * quantities[item.id];
  }, 0);
  const totalQty = selectedItems.reduce((sum, item) => sum + quantities[item.id], 0);

  const filteredCatalog = search.trim()
    ? catalog.filter(i => i.name?.toLowerCase().includes(search.toLowerCase()))
    : catalog;

  async function handleSell() {
    if (selectedItems.length === 0) return setError("Select at least one item.");
    for (const item of selectedItems) {
      const avail = getStockQty(item.id);
      if (quantities[item.id] > avail) {
        return setError(`Not enough stock for "${item.name}" (available: ${avail})`);
      }
    }
    setSaving(true);
    setError("");

    const vendorName = vendorProfile
      ? `${vendorProfile.first_name || ""} ${vendorProfile.last_name || ""}`.trim() || currentUser.email
      : (currentUser.displayName || currentUser.email);

    const saleItems = selectedItems.map(item => ({
      item_id:       item.id,
      item_name:     item.name,
      quantity:      quantities[item.id],
      unit:          item.unit || "",
      selling_price: item.selling_price || 0,
      subtotal:      (item.selling_price || 0) * quantities[item.id],
    }));

    try {
      // ── Offline path ───────────────────────────────────────────────
      if (!navigator.onLine) {
        const saleRecord = await savePendingSale({
          vendorId:    currentUser.uid,
          vendorName,
          clientId:    client?.id   || null,
          clientName,
          items:       saleItems,
          totalAmount,
        });

        // Deduct from IndexedDB immediately
        for (const item of selectedItems) {
          await updateStockItemOffline(item.id, -quantities[item.id]).catch(() => {});
          setStockItems(prev => prev.map(s =>
            s.id === item.id
              ? { ...s, quantity: Math.max(0, (s.quantity || 0) - quantities[item.id]) }
              : s
          ));
        }

        setReceipt({
          saleId:         saleRecord.localId,
          isOffline:      true,
          clientName,
          vendorName,
          vendorPhone:    vendorProfile?.phone || "",
          companyName:    companyInfo.name,
          companyContact: companyInfo.contact,
          items:          saleItems,
          totalAmount,
          totalItems:     totalQty,
          date:           new Date().toLocaleString(),
        });
        setSaving(false);
        return;
      }

      // ── Online path (unchanged) ────────────────────────────────────
      // Get vendor GPS (optional)
      let saleLocation = null;
      try {
        const pos = await new Promise((res, rej) =>
          navigator.geolocation.getCurrentPosition(res, rej, { timeout: 5000 })
        );
        saleLocation = { lat: pos.coords.latitude, lng: pos.coords.longitude };
      } catch (_) { /* location optional */ }

      const batch   = writeBatch(db);
      const saleRef = doc(collection(db, "companies", companyId, "sales"));

      batch.set(saleRef, {
        vendor_id:    currentUser.uid,
        vendor_name:  vendorName,
        client_id:    client?.id   || null,
        client_name:  clientName,
        items:        saleItems,
        total_items:  saleItems.reduce((s, i) => s + i.quantity, 0),
        total_amount: totalAmount,
        created_at:   serverTimestamp(),
        location:     saleLocation,
      });

      for (const item of selectedItems) {
        const currentQty = getStockQty(item.id);
        const newQty     = currentQty - quantities[item.id];
        const stockRef   = doc(db, "companies", companyId, "vendor_stock", currentUser.uid, "items", item.id);
        batch.update(stockRef, { quantity: newQty, updated_at: serverTimestamp() });

        const movRef = doc(collection(db, "companies", companyId, "stock_movements"));
        batch.set(movRef, {
          type:         "sale",
          vendor_id:    currentUser.uid,
          vendor_name:  vendorName,
          product_id:   item.id,
          product_name: item.name,
          quantity:     quantities[item.id],
          sale_id:      saleRef.id,
          note:         `Sale to ${clientName || "client"}`,
          created_at:   serverTimestamp(),
        });
      }

      await batch.commit();

      setReceipt({
        saleId:         saleRef.id,
        isOffline:      false,
        clientName,
        vendorName,
        vendorPhone:    vendorProfile?.phone || "",
        companyName:    companyInfo.name,
        companyContact: companyInfo.contact,
        items:          saleItems,
        totalAmount,
        totalItems:     totalQty,
        date:           new Date().toLocaleString(),
      });
    } catch (err) {
      setError("Error: " + err.message + (err.code ? ` (${err.code})` : ""));
    }
    setSaving(false);
  }

  // ── Receipt screen ──────────────────────────────────────────────────────────
  if (receipt) {
    return (
      <>
        <style>{`
          @media print {
            @page { size: auto; margin: 8mm; }
            body * { visibility: hidden !important; }
            #receipt-print-area, #receipt-print-area * { visibility: visible !important; }
            #receipt-print-area { position: absolute; top: 0; left: 0; width: 100%; border: none !important; border-radius: 0 !important; }
          }
        `}</style>
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
          <div className="absolute inset-0 bg-black/60" />
          <div className="relative bg-white rounded-t-3xl sm:rounded-2xl w-full sm:max-w-sm max-h-[90vh] flex flex-col shadow-2xl">
            <div className="flex items-center justify-between px-5 pt-5 pb-3 shrink-0">
              <div className="flex items-center gap-2">
                <Receipt className="w-5 h-5 text-green-600" />
                <h2 className="text-base font-bold text-gray-800">Sale Complete</h2>
              </div>
              <button onClick={onClose}
                className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 pb-3">
              <div id="receipt-print-area" className="border border-gray-200 rounded-xl p-4 space-y-3">
                {/* Header */}
                <div className="text-center border-b border-dashed border-gray-200 pb-3">
                  {receipt.companyName && (
                    <p className="font-extrabold text-gray-900 text-lg leading-tight">{receipt.companyName}</p>
                  )}
                  {receipt.companyContact && (
                    <p className="text-xs text-gray-500 mt-0.5">📞 {receipt.companyContact}</p>
                  )}
                  <p className="font-bold text-gray-700 text-sm mt-2">Sales Receipt</p>
                  <p className="text-xs text-gray-400">{receipt.date}</p>
                  <p className="text-xs text-gray-400 mt-0.5">#{receipt.saleId.slice(-8).toUpperCase()}</p>
                  {receipt.isOffline && (
                    <span className="inline-flex items-center gap-1 mt-1.5 text-xs bg-orange-100 text-orange-600 px-2 py-0.5 rounded-full">
                      <WifiOff className="w-3 h-3" /> Offline — will sync when connected
                    </span>
                  )}
                </div>
                {/* Info */}
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between text-gray-600">
                    <span>Client:</span>
                    <span className="font-semibold text-gray-800">{receipt.clientName || "—"}</span>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>Vendor:</span>
                    <span className="font-semibold text-gray-800">{receipt.vendorName}</span>
                  </div>
                  {receipt.vendorPhone && (
                    <div className="flex justify-between text-gray-600">
                      <span>Vendor Tel:</span>
                      <span className="font-semibold text-gray-800">{receipt.vendorPhone}</span>
                    </div>
                  )}
                </div>
                {/* Items */}
                <div className="border-t border-dashed border-gray-200 pt-3 space-y-2">
                  {receipt.items.map((item, i) => (
                    <div key={i} className="flex items-start justify-between text-sm">
                      <div>
                        <p className="font-medium text-gray-800">{item.item_name}</p>
                        <p className="text-xs text-gray-400">{item.quantity} {item.unit} × {item.selling_price > 0 ? item.selling_price.toFixed(2) : "—"}</p>
                      </div>
                      {item.selling_price > 0 && (
                        <span className="font-semibold text-gray-800">{item.subtotal.toFixed(2)}</span>
                      )}
                    </div>
                  ))}
                </div>
                {/* Total */}
                {receipt.totalAmount > 0 && (
                  <div className="border-t border-gray-200 pt-3 flex justify-between font-bold text-base text-gray-900">
                    <span>Total</span>
                    <span>{receipt.totalAmount.toFixed(2)}</span>
                  </div>
                )}
                <div className="text-center pt-2">
                  <p className="text-xs text-gray-400">Thank you for your business!</p>
                </div>
              </div>
            </div>

            <div className="px-5 pb-6 pt-2 shrink-0 flex gap-3">
              <button onClick={() => window.print()}
                className="flex-1 flex items-center justify-center gap-2 border border-gray-200 text-gray-600 font-medium py-3.5 rounded-2xl text-sm active:bg-gray-50">
                <Printer className="w-4 h-4" /> Print
              </button>
              <button onClick={onClose}
                className="flex-1 bg-green-600 text-white font-bold py-3.5 rounded-2xl flex items-center justify-center gap-2 text-sm">
                <CheckCircle className="w-4 h-4" /> Done
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // ── POS screen ──────────────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-t-3xl sm:rounded-2xl w-full sm:max-w-sm max-h-[90vh] flex flex-col shadow-2xl">

        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-3 shrink-0">
          <div className="flex items-center gap-2">
            <ShoppingCart className="w-5 h-5 text-yellow-500" />
            <h2 className="text-base font-bold text-gray-800">Sell Items</h2>
          </div>
          <button onClick={onClose}
            className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        {/* Client + search */}
        <div className="px-5 pb-3 shrink-0 space-y-2">
          <p className="text-sm text-gray-500">Client: <span className="font-semibold text-gray-700">{clientName || "—"}</span></p>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search items..."
              className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400" />
          </div>
        </div>

        {/* Item list */}
        <div className="flex-1 overflow-y-auto px-5 pb-3 space-y-2">
          {loading && <div className="py-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>}

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-3 text-sm flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 shrink-0" />{error}
            </div>
          )}

          {!loading && filteredCatalog.map(item => {
            const avail = getStockQty(item.id);
            const qty   = quantities[item.id] || 0;
            const price = item.selling_price || 0;
            return (
              <div key={item.id}
                className={`flex items-center gap-3 rounded-xl p-3 border transition ${qty > 0 ? "bg-yellow-50 border-yellow-200" : "bg-gray-50 border-transparent"}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-800">{item.name}</p>
                  <p className="text-xs text-gray-400">
                    {item.unit}{price > 0 ? ` · ${price.toFixed(2)} each` : ""} · {avail} avail
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => setQuantities(p => ({ ...p, [item.id]: Math.max(0, (p[item.id] || 0) - 1) }))}
                    className="w-9 h-9 rounded-full bg-white border border-gray-200 flex items-center justify-center text-gray-600 active:bg-gray-100 text-lg font-bold">
                    −
                  </button>
                  <span className="w-8 text-center font-bold text-gray-800 text-sm">{qty}</span>
                  <button
                    onClick={() => setQuantities(p => ({ ...p, [item.id]: Math.min(avail, (p[item.id] || 0) + 1) }))}
                    disabled={avail === 0}
                    className="w-9 h-9 rounded-full bg-white border border-gray-200 flex items-center justify-center text-gray-600 active:bg-gray-100 disabled:opacity-40 text-lg font-bold">
                    +
                  </button>
                </div>
              </div>
            );
          })}

          {!loading && filteredCatalog.length === 0 && (
            <p className="text-gray-400 text-sm text-center py-6">No items found</p>
          )}
        </div>

        {/* Summary bar */}
        {totalQty > 0 && (
          <div className="mx-5 mb-2 bg-yellow-50 border border-yellow-200 rounded-xl px-4 py-3 flex items-center justify-between shrink-0">
            <span className="text-sm text-yellow-700 font-medium">{totalQty} item{totalQty !== 1 ? "s" : ""} selected</span>
            {totalAmount > 0 && (
              <span className="text-base font-bold text-yellow-800">Total: {totalAmount.toFixed(2)}</span>
            )}
          </div>
        )}

        {/* Action buttons */}
        <div className="px-5 pb-6 pt-1 shrink-0 flex gap-3">
          <button onClick={onClose}
            className="flex-1 border border-gray-200 text-gray-600 font-medium py-3.5 rounded-2xl text-sm">
            Cancel
          </button>
          <button onClick={handleSell} disabled={saving || loading || totalQty === 0}
            className="flex-1 bg-yellow-400 hover:bg-yellow-500 disabled:opacity-50 text-gray-900 font-bold py-3.5 rounded-2xl flex items-center justify-center gap-2 transition text-sm">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckSquare className="w-4 h-4" />}
            {saving ? "Processing..." : "Complete Sale"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Reusable Receipt Modal — used in StockPage and RoutePage
// ─────────────────────────────────────────────────────────────────────────────
function ReceiptModal({ sale, companyId, vendorProfile, onClose }) {
  const [companyInfo, setCompanyInfo] = useState({ name: "", contact: "" });

  useEffect(() => {
    if (!companyId) return;
    async function loadInfo() {
      try {
        const [rootSnap, profileSnap] = await Promise.all([
          getDoc(doc(db, "companies", companyId)),
          getDoc(doc(db, "companies", companyId, "profile", "info")),
        ]);
        setCompanyInfo({
          name:    rootSnap.exists()    ? (rootSnap.data().name    || "") : "",
          contact: profileSnap.exists() ? (profileSnap.data().phone || "") : "",
        });
      } catch (_) {}
    }
    loadInfo();
  }, [companyId]);

  if (!sale) return null;
  const date = sale.created_at
    ? (sale.created_at.toDate ? sale.created_at.toDate() : new Date(sale.created_at)).toLocaleString()
    : "—";
  const vendorPhone = sale.vendor_phone || vendorProfile?.phone || "";

  return (
    <>
      <style>{`
        @media print {
          @page { size: auto; margin: 8mm; }
          body * { visibility: hidden !important; }
          #receipt-print-area, #receipt-print-area * { visibility: visible !important; }
          #receipt-print-area { position: absolute; top: 0; left: 0; width: 100%; border: none !important; border-radius: 0 !important; }
        }
      `}</style>
      <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
        <div className="absolute inset-0 bg-black/60" onClick={onClose} />
        <div className="relative bg-white rounded-t-3xl sm:rounded-2xl w-full sm:max-w-sm max-h-[90vh] flex flex-col shadow-2xl">
          <div className="flex items-center justify-between px-5 pt-5 pb-3 shrink-0">
            <div className="flex items-center gap-2">
              <Receipt className="w-5 h-5 text-green-600" />
              <h2 className="text-base font-bold text-gray-800">Receipt</h2>
            </div>
            <button onClick={onClose}
              className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
              <X className="w-4 h-4 text-gray-500" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto px-5 pb-3">
            <div id="receipt-print-area" className="border border-gray-200 rounded-xl p-4 space-y-3">
              {/* Header */}
              <div className="text-center border-b border-dashed border-gray-200 pb-3">
                {companyInfo.name && (
                  <p className="font-extrabold text-gray-900 text-lg leading-tight">{companyInfo.name}</p>
                )}
                {companyInfo.contact && (
                  <p className="text-xs text-gray-500 mt-0.5">📞 {companyInfo.contact}</p>
                )}
                <p className="font-bold text-gray-700 text-sm mt-2">Sales Receipt</p>
                <p className="text-xs text-gray-400">{date}</p>
                <p className="text-xs text-gray-400 mt-0.5">#{sale.id.slice(-8).toUpperCase()}</p>
              </div>
              {/* Info */}
              <div className="space-y-1 text-sm">
                <div className="flex justify-between text-gray-600">
                  <span>Client:</span>
                  <span className="font-semibold text-gray-800">{sale.client_name || "—"}</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>Vendor:</span>
                  <span className="font-semibold text-gray-800">{sale.vendor_name || "—"}</span>
                </div>
                {vendorPhone && (
                  <div className="flex justify-between text-gray-600">
                    <span>Vendor Tel:</span>
                    <span className="font-semibold text-gray-800">{vendorPhone}</span>
                  </div>
                )}
              </div>
              {/* Items */}
              <div className="border-t border-dashed border-gray-200 pt-3 space-y-2">
                {(sale.items || []).map((item, i) => (
                  <div key={i} className="flex items-start justify-between text-sm">
                    <div>
                      <p className="font-medium text-gray-800">{item.item_name}</p>
                      <p className="text-xs text-gray-400">
                        {item.quantity} {item.unit} × {item.selling_price > 0 ? item.selling_price.toFixed(2) : "—"}
                      </p>
                    </div>
                    {item.selling_price > 0 && (
                      <span className="font-semibold text-gray-800">{item.subtotal.toFixed(2)}</span>
                    )}
                  </div>
                ))}
              </div>
              {/* Total */}
              {sale.total_amount > 0 && (
                <div className="border-t border-gray-200 pt-3 flex justify-between font-bold text-base text-gray-900">
                  <span>Total</span>
                  <span>{sale.total_amount.toFixed(2)}</span>
                </div>
              )}
              <div className="text-center pt-2">
                <p className="text-xs text-gray-400">Thank you for your business!</p>
              </div>
            </div>
          </div>
          <div className="px-5 pb-6 pt-2 shrink-0 flex gap-3">
            <button onClick={() => window.print()}
              className="flex-1 flex items-center justify-center gap-2 border border-gray-200 text-gray-600 font-medium py-3.5 rounded-2xl text-sm active:bg-gray-50">
              <Printer className="w-4 h-4" /> Print
            </button>
            <button onClick={onClose}
              className="flex-1 bg-green-600 text-white font-bold py-3.5 rounded-2xl flex items-center justify-center gap-2 text-sm">
              <X className="w-4 h-4" /> Close
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

export default VendorDashboard;
